import type {
  AppointmentDetail,
  AppointmentFilterParam,
  AppointmentPayload,
  BillingStatus,
  BoardingBillableItemsPayload,
  BoardingBillableItemWrite,
  BoardingCheckInPayload,
  BoardingCheckInResult,
  BoardingCheckOutPayload,
  BoardingCheckOutResult,
  BoardingPaymentPayload,
  BoardingPaymentResult,
  BoardingDetail,
  BoardingRecordFilterParam,
  BoardingRecordListItem,
  BoardingRecordStatus,
  BoardingReservationPayload,
  BoardingOccupancyStatus,
  CaseSheetDetail,
  CaseSheetFilterParam,
  CaseSheetPayload,
  HealthcareGuardianSummary,
  HealthcarePetSummary,
  HealthcarePriority,
  HealthcareServiceCreatePayload,
  HealthcareServiceFilterParam,
  HealthcareServiceRecord,
  HealthcareServiceUpdatePayload,
  HealthcareStatus,
  Doctor,
  StartVisitResult,
  VisitDetail,
  VisitFilterParam,
  VisitHistoryItem,
  VisitLabRecord,
  VisitOrderItem,
  VisitRadiologyRecord,
  VisitServiceRecord,
  VisitUpsertPayload,
} from "@/type/healthcare";
import { asBoardingRecordId, asRoomId } from "@/type/healthcare";
import type { FrappeListResult } from "@/services/frappe/list";
import type { FrappeFilterClause } from "@/services/frappe/filters";
import type { AddEditPet, Pet, PetDropDown, PetGuardian } from "@/type/pet-profile";
import type {
  CreateGuardianPayload,
  Guardian,
  GuardianDropDown,
  GuardianProfileData,
} from "@/type/guardian";
import {
  AnimalGender,
  AnimalSpecies,
  AnimalType,
  BloodType,
  PetStatus,
} from "@/type/pet-profile";
import { getMockServiceRoomsSnapshot } from "@/api/serviceRoomApi";
import {
  applyConsultationTimerAction,
  consultationTimerBlock,
  type ConsultationTimerAction,
} from "@/utils/visitConsultationTimer";

const STORAGE_KEY = "alkokh-healthcare-mock-db-v2";
const WORKBENCH_STORAGE_KEY = "alkokh-healthcare-workbench-mock-v1";
const PET_PROFILE_STORAGE_KEY = "alkokh-pet-profile-mock-v1";

// Clinical-note catalogue options (mirror get_visit_workbench's top-level arrays, in sort_order).
const MOCK_ASSESSMENT_FINDING_OPTIONS = [
  { code: "dehydration_mild", label_en: "Mild dehydration", label_ar: "جفاف خفيف" },
  { code: "dehydration_severe", label_en: "Severe dehydration", label_ar: "جفاف شديد" },
  { code: "abdominal_pain", label_en: "Abdominal pain", label_ar: "ألم بطني" },
  { code: "fever", label_en: "Fever", label_ar: "حمى" },
  { code: "lethargy", label_en: "Lethargy", label_ar: "خمول" },
  { code: "weight_loss", label_en: "Weight loss", label_ar: "فقدان الوزن" },
];
const MOCK_CLIENT_OBSERVATION_OPTIONS = [
  { code: "vomiting", label_en: "Vomiting", label_ar: "قيء" },
  { code: "diarrhea", label_en: "Diarrhea", label_ar: "إسهال" },
  { code: "reduced_appetite", label_en: "Reduced appetite", label_ar: "قلة الشهية" },
  { code: "lethargy", label_en: "Lethargy", label_ar: "خمول" },
  { code: "coughing", label_en: "Coughing", label_ar: "سعال" },
];
const MOCK_OWNER_INSTRUCTION_OPTIONS = [
  { code: "bland_diet", label_en: "Feed a bland diet", label_ar: "تقديم نظام غذائي خفيف" },
  { code: "medication_as_directed", label_en: "Give medication as directed", label_ar: "إعطاء الدواء حسب التعليمات" },
  { code: "rest", label_en: "Ensure plenty of rest", label_ar: "ضمان الراحة الكافية" },
  { code: "return_if_worse", label_en: "Return if symptoms worsen", label_ar: "العودة إذا ساءت الأعراض" },
  { code: "recheck_visit", label_en: "Book a recheck visit", label_ar: "حجز زيارة إعادة فحص" },
];

interface HealthcareMockDb {
  appointments: AppointmentDetail[];
  caseSheets: CaseSheetDetail[];
  visits: VisitDetail[];
  services: VisitServiceRecord[];
  boarding: BoardingDetail[];
}

export interface HealthcareLookupOption<T = unknown> {
  id: string;
  label: string;
  subtitle?: string | null;
  image?: string | null;
  raw?: T;
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function withDelay<T>(value: T, delay = 180): Promise<T> {
  return new Promise(resolve => setTimeout(() => resolve(clone(value)), delay));
}

function getNowIso() {
  return new Date().toISOString();
}

function buildCaseSheetSections(caseSheet: {
  caseSheetNo: string;
  chiefComplaint?: string | null;
  priority?: HealthcarePriority | null;
  status?: HealthcareStatus | null;
  createdAt?: string | null;
  billingStatus?: BillingStatus | null;
  pet: HealthcarePetSummary;
  guardian: HealthcareGuardianSummary;
  intakeSummary?: string | null;
  symptoms?: string | null;
  background?: string | null;
  history?: string | null;
  triageNotes?: string | null;
}) {
  return [
    {
      title: "Intake",
      fields: [
        { label: "Case Sheet", value: caseSheet.caseSheetNo },
        { label: "Chief Complaint", value: caseSheet.chiefComplaint },
        { label: "Priority", value: caseSheet.priority },
        { label: "Status", value: caseSheet.status },
        { label: "Created", value: caseSheet.createdAt },
        { label: "Billing", value: caseSheet.billingStatus },
      ].filter(field => field.value),
    },
    {
      title: "Pet",
      fields: [
        { label: "Pet", value: caseSheet.pet.name },
        { label: "Species", value: caseSheet.pet.species },
        { label: "Breed", value: caseSheet.pet.breed },
        { label: "Gender", value: caseSheet.pet.gender },
        { label: "Age", value: caseSheet.pet.age },
        { label: "Weight", value: caseSheet.pet.weight },
      ].filter(field => field.value),
    },
    {
      title: "Owner",
      fields: [
        { label: "Owner", value: caseSheet.guardian.name },
        { label: "Phone", value: caseSheet.guardian.phone },
        { label: "Email", value: caseSheet.guardian.email },
      ].filter(field => field.value),
    },
    {
      title: "Clinical Background",
      fields: [
        { label: "Intake Summary", value: caseSheet.intakeSummary },
        { label: "Symptoms", value: caseSheet.symptoms },
        { label: "Background", value: caseSheet.background },
        { label: "History", value: caseSheet.history },
        { label: "Triage Notes", value: caseSheet.triageNotes },
      ].filter(field => field.value),
    },
  ].filter(section => section.fields.length);
}

function createPet(
  id: string,
  name: string,
  species: string,
  breed: string,
  gender: string,
  age: string,
): HealthcarePetSummary {
  return {
    id,
    name,
    species,
    breed,
    gender,
    age,
    image: null,
    weight: null,
  };
}

function createGuardian(
  id: string,
  name: string,
  phone: string,
  email: string,
): HealthcareGuardianSummary {
  return {
    id,
    name,
    phone,
    email,
    image: null,
  };
}

function seedDb(): HealthcareMockDb {
  const pets = {
    luna: createPet("PET-001", "Luna", "Cat", "British Shorthair", "Female", "3 years"),
    rocky: createPet("PET-002", "Rocky", "Dog", "Golden Retriever", "Male", "4 years"),
    milo: createPet("PET-003", "Milo", "Cat", "Siamese", "Male", "2 years"),
    bella: createPet("PET-004", "Bella", "Dog", "Pomeranian", "Female", "5 years"),
    coco: createPet("PET-005", "Coco", "Rabbit", "Mini Lop", "Female", "1 year"),
  };

  const guardians = {
    sarah: createGuardian("GUARD-001", "Sarah Ahmed", "07700000001", "sarah@example.com"),
    omar: createGuardian("GUARD-002", "Omar Hassan", "07700000002", "omar@example.com"),
    layla: createGuardian("GUARD-003", "Layla Kareem", "07700000003", "layla@example.com"),
    noor: createGuardian("GUARD-004", "Noor Ali", "07700000004", "noor@example.com"),
    zain: createGuardian("GUARD-005", "Zain Abbas", "07700000005", "zain@example.com"),
  };

  pets.luna.weight = "4.2 kg";
  pets.rocky.weight = "28.4 kg";
  pets.milo.weight = "4.9 kg";
  pets.bella.weight = "3.6 kg";
  pets.coco.weight = "1.8 kg";

  const caseSheetsBase = [
    {
      id: "CASE-001",
      caseSheetNo: "VCS-24001",
      pet: pets.luna,
      guardian: guardians.sarah,
      chiefComplaint: "Vomiting and decreased appetite",
      intakeSummary: "Started vomiting last night with mild lethargy.",
      priority: "High",
      status: "Waiting Doctor",
      billingStatus: "Pending",
      createdAt: "2026-04-18 09:10:00",
      linkedVisitId: null,
      symptoms: "Vomiting, lethargy, reduced appetite",
      background: "Indoor cat. No recent diet change.",
      history: "Previous gastritis episode six months ago.",
      triageNotes: "Hydration slightly low.",
      requestedBy: guardians.sarah.name,
      practitionerName: null,
    },
    {
      id: "CASE-002",
      caseSheetNo: "VCS-24002",
      pet: pets.rocky,
      guardian: guardians.omar,
      chiefComplaint: "Limping on rear left leg",
      intakeSummary: "Limp started after outdoor play.",
      priority: "Normal",
      status: "In Consultation",
      billingStatus: "Unbilled",
      createdAt: "2026-04-18 10:40:00",
      linkedVisitId: "VIS-001",
      symptoms: "Pain while walking, mild swelling",
      background: "Active dog, no known trauma observed.",
      history: "No previous orthopedic issues.",
      triageNotes: "Stable vitals.",
      requestedBy: guardians.omar.name,
      practitionerName: "doctor@example.com",
    },
    {
      id: "CASE-003",
      caseSheetNo: "VCS-24003",
      pet: pets.milo,
      guardian: guardians.layla,
      chiefComplaint: "Skin itching and hair loss",
      intakeSummary: "Localized itching for two weeks.",
      priority: "Normal",
      status: "Draft",
      billingStatus: "Unbilled",
      createdAt: "2026-04-17 13:20:00",
      linkedVisitId: null,
      symptoms: "Itching, patchy hair loss",
      background: "Recently boarded for four days.",
      history: "No chronic conditions.",
      triageNotes: "Needs skin scrape test.",
      requestedBy: guardians.layla.name,
      practitionerName: null,
    },
    {
      id: "CASE-004",
      caseSheetNo: "VCS-24004",
      pet: pets.bella,
      guardian: guardians.noor,
      chiefComplaint: "Routine post-op check",
      intakeSummary: "Follow-up after dental cleaning.",
      priority: "Low",
      status: "Closed",
      billingStatus: "Paid",
      createdAt: "2026-04-16 15:00:00",
      linkedVisitId: "VIS-002",
      symptoms: "None",
      background: "Routine recovery review.",
      history: "Dental cleaning last week.",
      triageNotes: "Recovery looks good.",
      requestedBy: guardians.noor.name,
      practitionerName: "doctor@example.com",
    },
    {
      id: "CASE-005",
      caseSheetNo: "VCS-24005",
      pet: pets.coco,
      guardian: guardians.zain,
      chiefComplaint: "Decreased stool output",
      intakeSummary: "Possible GI slowdown, still active.",
      priority: "Urgent",
      status: "Waiting Doctor",
      billingStatus: "Pending",
      createdAt: "2026-04-19 08:25:00",
      linkedVisitId: null,
      symptoms: "Reduced stool, reduced appetite",
      background: "Rabbit on pellet-heavy diet.",
      history: "No previous GI stasis.",
      triageNotes: "Urgent check recommended.",
      requestedBy: guardians.zain.name,
      practitionerName: null,
    },
  ];

  const caseSheets: CaseSheetDetail[] = caseSheetsBase.map(item => ({
    ...item,
    linkedVisitStatus: item.linkedVisitId ? "In Consultation" : null,
    sections: buildCaseSheetSections(item),
  }));

  const visits: VisitDetail[] = [
    {
      id: "VIS-001",
      caseSheetId: "CASE-002",
      caseSheetNo: "VCS-24002",
      status: "In Consultation",
      priority: "Normal",
      practitionerId: "doctor@example.com",
      practitionerName: "doctor@example.com",
      practitionerImage: null,
      visitDate: "2026-04-18",
      visitTime: "10:55",
      startedAt: "2026-04-18 10:55:00",
      pet: pets.rocky,
      guardian: guardians.omar,
      chiefComplaint: "Limping on rear left leg",
      intakeSummary: "Limp started after outdoor play.",
      overview: "<p>Rocky presented with mild rear left leg lameness after active play.</p>",
      examination: "<p>Mild swelling over stifle. Pain response on extension.</p>",
      assessment: "<p>Likely soft tissue strain. Rule out ligament injury.</p>",
      plan: "<p>Rest, anti-inflammatory support, recheck in 5 days.</p>",
      instructions: "<p>Restrict jumping and running at home.</p>",
      orders: [
        {
          id: "ORD-001",
          title: "Pain relief injection",
          kind: "service",
          status: "Completed",
          qty: 1,
          price: 25000,
          itemCode: "VET-INJ",
          category: "Treatment",
          priority: "Normal",
          linkedServiceId: "SVC-INJECTION",
          note: "Administered during consultation.",
        },
        {
          id: "ORD-002",
          title: "Limb X-ray",
          kind: "radiology",
          status: "Ordered",
          qty: 1,
          price: 50000,
          itemCode: "RAD-XRAY-LIMB",
          category: "X-ray",
          priority: "High",
          note: "Two-view rear left limb radiograph if lameness continues after initial exam.",
        },
        {
          id: "ORD-003",
          title: "CBC panel",
          kind: "lab",
          status: "Ordered",
          qty: 1,
          price: 28000,
          itemCode: "LAB-CBC",
          category: "Hematology",
          priority: "Normal",
          note: "Baseline screen before anti-inflammatory course.",
        },
      ],
      labRecords: [
        {
          id: "LABREC-001",
          visitId: "VIS-001",
          orderId: "ORD-003",
          definitionId: "LAB-CBC",
          testName: "CBC panel",
          code: "LAB-CBC",
          specimen: "Whole blood",
          status: "Awaiting Sample",
          priority: "Normal",
          orderedAt: "2026-04-18 11:05:00",
          startAt: null,
          endAt: null,
          releasedAt: null,
        },
      ],
      radiologyRecords: [
        {
          id: "RADREC-001",
          visitId: "VIS-001",
          orderId: "ORD-002",
          definitionId: "RAD-XRAY-LIMB",
          studyName: "Limb X-ray",
          code: "RAD-XRAY-LIMB",
          modality: "X-ray",
          bodyPart: "Limb",
          status: "Ordered",
          priority: "High",
          orderedAt: "2026-04-18 11:06:00",
          resultEnteredAt: null,
        },
      ],
      serviceRecords: [
        {
          id: "SVCREC-001",
          visitId: "VIS-001",
          orderId: "ORD-001",
          definitionId: "SVC-INJECTION",
          serviceName: "Pain relief injection",
          code: "VET-INJ",
          status: "Completed",
          assignedTo: "doctor@example.com",
          note: "Administered during consultation.",
          price: 25000,
          performedAt: "2026-04-18 11:02:00",
        },
      ],
      billing: {
        status: "Pending",
        currency: "IQD",
        total: 103000,
        deposit: 0,
        paid: 0,
        balance: 103000,
      },
      historyItems: [],
      linkedCaseSheet: caseSheets.find(item => item.id === "CASE-002") || null,
    },
    {
      id: "VIS-003",
      caseSheetId: null,
      caseSheetNo: "VCS-23971",
      status: "Closed",
      priority: "Low",
      practitionerId: "doctor@example.com",
      practitionerName: "Dr. Rana Yousif",
      practitionerImage: null,
      visitDate: "2026-03-09",
      visitTime: "12:20",
      startedAt: "2026-03-09 12:20:00",
      pet: pets.rocky,
      guardian: guardians.omar,
      chiefComplaint: "Annual vaccination and wellness review",
      intakeSummary: "Guardian requested yearly vaccine update and general check.",
      overview: "<p>Bright, alert, responsive. Normal appetite and activity.</p>",
      examination: "<p>Normal heart and lung sounds. No orthopedic pain at that time.</p>",
      assessment: "<p>Healthy adult dog. Vaccination completed.</p>",
      plan: "<p>Return for wellness review in 12 months.</p>",
      instructions: "<p>Monitor for mild vaccine soreness for 24 hours.</p>",
      orders: [
        {
          id: "ORD-010",
          title: "Doctor consultation",
          kind: "service",
          status: "Completed",
          qty: 1,
          price: 25000,
          itemCode: "VET-CONSULT",
          category: "Clinical service",
          priority: "Routine",
          linkedServiceId: "SVC-CONSULT",
          note: "Wellness exam.",
        },
        {
          id: "ORD-011",
          title: "Follow-up reminder",
          kind: "other",
          status: "Completed",
          qty: 1,
          price: 0,
          itemCode: "FOLLOWUP",
          category: "Care coordination",
          priority: "Routine",
          note: "Annual vaccine reminder scheduled.",
        },
        {
          id: "ORD-012",
          title: "Thoracic X-ray",
          kind: "radiology",
          status: "Released",
          qty: 1,
          price: 45000,
          itemCode: "RAD-XRAY-THORAX",
          category: "X-ray",
          priority: "Routine",
          note: "Wellness baseline study.",
        },
      ],
      serviceRecords: [
        {
          id: "SVCREC-010",
          visitId: "VIS-003",
          orderId: "ORD-010",
          definitionId: "SVC-CONSULT",
          serviceName: "Doctor consultation",
          code: "VET-CONSULT",
          status: "Completed",
          assignedTo: "Dr. Rana Yousif",
          note: "Wellness exam.",
          price: 25000,
          performedAt: "2026-03-09 12:25:00",
        },
      ],
      labRecords: [
        {
          id: "LABREC-013",
          visitId: "VIS-003",
          orderId: "ORD-013",
          definitionId: "LAB-CBC",
          testName: "Complete blood count",
          code: "LAB-CBC",
          specimen: "Whole blood (EDTA)",
          petId: pets.rocky.id,
          petName: pets.rocky.name,
          petType: pets.rocky.animalType,
          petBreed: pets.rocky.breed,
          guardianId: guardians.omar.id,
          guardianName: guardians.omar.name,
          doctor: "doctor@example.com",
          doctorName: "Dr. Rana Yousif",
          status: "Released",
          priority: "Routine",
          result: "Red blood cell indices, white blood cell differential, and platelet count are within expected reference ranges. No hematologic abnormality is identified.",
          resultVisibility: "Guardian Visible",
          orderedAt: "2026-03-09 12:27:00",
          startAt: "2026-03-09 12:34:00",
          sampleCollectedAt: "2026-03-09 12:36:00",
          resultEnteredAt: "2026-03-09 13:00:00",
          releasedAt: "2026-03-09 13:18:00",
          attachments: [
            {
              file_url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1200' height='800'%3E%3Crect width='1200' height='800' fill='%23f7f4ff'/%3E%3Ctext x='70' y='100' fill='%234c3c76' font-size='54' font-family='Arial'%3ECBC RESULT%3C/text%3E%3Cpath d='M90 650L250 520L400 580L570 340L730 430L900 210L1110 290' fill='none' stroke='%237c4dff' stroke-width='18'/%3E%3Cline x1='90' y1='680' x2='1110' y2='680' stroke='%23938ca7' stroke-width='8'/%3E%3C/svg%3E",
              file_name: "cbc-result-chart.svg",
              name: "FILE-LAB-013-IMAGE",
              is_image: 1,
            },
            {
              file_url: "/private/files/cbc-analyzer-export.csv",
              file_name: "cbc-analyzer-export.csv",
              name: "FILE-LAB-013-CSV",
              is_image: 0,
            },
          ],
        },
      ],
      radiologyRecords: [
        {
          id: "RADREC-012",
          visitId: "VIS-003",
          orderId: "ORD-012",
          definitionId: "RAD-XRAY-THORAX",
          studyName: "Thoracic X-ray",
          code: "RAD-XRAY-THORAX",
          petId: pets.rocky.id,
          petName: pets.rocky.name,
          petType: pets.rocky.animalType,
          petBreed: pets.rocky.breed,
          guardianId: guardians.omar.id,
          guardianName: guardians.omar.name,
          doctor: "doctor@example.com",
          doctorName: "Dr. Rana Yousif",
          modality: "X-ray",
          bodyPart: "Thorax",
          status: "Released",
          priority: "Routine",
          report: "Cardiac silhouette is within normal limits. Pulmonary fields are clear. No pleural effusion or focal consolidation is identified.",
          resultVisibility: "Guardian Visible",
          orderedAt: "2026-03-09 12:28:00",
          resultEnteredAt: "2026-03-09 13:05:00",
          releasedAt: "2026-03-09 13:20:00",
          attachments: [
            {
              file_url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1200' height='800'%3E%3Crect width='1200' height='800' fill='%23131720'/%3E%3Cellipse cx='600' cy='400' rx='310' ry='330' fill='%233f4654' stroke='%23e8edf5' stroke-width='18'/%3E%3Cpath d='M430 170L500 630M770 170L700 630M330 310L870 310M310 480L890 480' stroke='%23d9dee8' stroke-width='13' opacity='.78'/%3E%3Ccircle cx='600' cy='405' r='95' fill='%23707786' stroke='%23f4f6fa' stroke-width='10'/%3E%3Ctext x='60' y='90' fill='%23ffffff' font-size='42' font-family='Arial'%3ETHORAX AP%3C/text%3E%3C/svg%3E",
              file_name: "thorax-ap.svg",
              name: "FILE-RAD-012-IMAGE",
              is_image: 1,
            },
            {
              file_url: "/private/files/thorax-series.dcm",
              file_name: "thorax-series.dcm",
              name: "FILE-RAD-012-DICOM",
              is_image: 0,
            },
          ],
        },
      ],
      billing: {
        status: "Paid",
        currency: "IQD",
        total: 25000,
        deposit: 0,
        paid: 25000,
        balance: 0,
      },
      historyItems: [],
      linkedCaseSheet: null,
    },
    {
      id: "VIS-002",
      caseSheetId: "CASE-004",
      caseSheetNo: "VCS-24004",
      status: "Closed",
      priority: "Low",
      practitionerId: "doctor@example.com",
      practitionerName: "doctor@example.com",
      practitionerImage: null,
      visitDate: "2026-04-16",
      visitTime: "15:15",
      startedAt: "2026-04-16 15:15:00",
      pet: pets.bella,
      guardian: guardians.noor,
      chiefComplaint: "Routine post-op check",
      intakeSummary: "Follow-up after dental cleaning.",
      overview: "<p>Post-procedure recheck with good appetite and activity.</p>",
      examination: "<p>Oral cavity healing well. No swelling or discharge.</p>",
      assessment: "<p>Recovery progressing normally.</p>",
      plan: "<p>Continue soft diet for two more days.</p>",
      instructions: "<p>Return if appetite drops or oral discomfort appears.</p>",
      orders: [],
      labRecords: [],
      radiologyRecords: [],
      serviceRecords: [],
      billing: {
        status: "Paid",
        currency: "IQD",
        total: 30000,
        deposit: 0,
        paid: 30000,
        balance: 0,
      },
      historyItems: [],
      linkedCaseSheet: caseSheets.find(item => item.id === "CASE-004") || null,
    },
  ];

  const appointments: AppointmentDetail[] = [
    {
      id: "APT-001",
      customerName: "Sarah Ahmed",
      customerEmail: "sarah@example.com",
      customerPhoneNumber: guardians.sarah.phone,
      customerDetails: "Follow-up for appetite loss",
      petId: pets.luna.id,
      guardianId: guardians.sarah.id,
      status: "Open",
      pet: pets.luna,
      guardian: guardians.sarah,
      scheduledTime: "2026-04-28 09:00:00",
      scheduledDate: "2026-04-28",
      scheduledClock: "09:00",
      customAppointmentType: "visit",
      customPet: pets.luna.id,
      customGuardian: guardians.sarah.id,
      customCustomer: "CUST-SARAH",
      selectedServiceTemplateId: "SVC-CONSULT",
      selectedServiceTemplateName: "Doctor consultation",
      doctorId: null,
      doctorName: null,
      linkedVisitId: null,
      linkedServiceId: null,
      convertedTarget: null,
      convertedAt: null,
      createdAt: "2026-04-27 15:10:00",
    },
    {
      id: "APT-002",
      customerName: "Zain Kareem",
      customerEmail: "zain@example.com",
      customerPhoneNumber: guardians.zain.phone,
      customerDetails: "Observation service booking",
      petId: pets.coco.id,
      guardianId: guardians.zain.id,
      status: "Open",
      pet: pets.coco,
      guardian: guardians.zain,
      scheduledTime: "2026-04-28 11:30:00",
      scheduledDate: "2026-04-28",
      scheduledClock: "11:30",
      customAppointmentType: "showering",
      customPet: pets.coco.id,
      customGuardian: guardians.zain.id,
      customCustomer: "CUST-ZAIN",
      selectedServiceTemplateId: "SVC-OBS",
      selectedServiceTemplateName: "Observation stay",
      doctorId: null,
      doctorName: null,
      linkedVisitId: null,
      linkedServiceId: null,
      convertedTarget: null,
      convertedAt: null,
      createdAt: "2026-04-27 16:25:00",
    },
    {
      id: "APT-003",
      customerName: "Omar Hassan",
      customerEmail: "omar@example.com",
      customerPhoneNumber: guardians.omar.phone,
      customerDetails: "Limping on rear left leg",
      petId: pets.rocky.id,
      guardianId: guardians.omar.id,
      status: "Closed",
      pet: pets.rocky,
      guardian: guardians.omar,
      scheduledTime: "2026-04-18 10:30:00",
      scheduledDate: "2026-04-18",
      scheduledClock: "10:30",
      customAppointmentType: "visit",
      customPet: pets.rocky.id,
      customGuardian: guardians.omar.id,
      customCustomer: "CUST-OMAR",
      selectedServiceTemplateId: "SVC-CONSULT",
      selectedServiceTemplateName: "Doctor consultation",
      doctorId: "doctor@example.com",
      doctorName: "doctor@example.com",
      linkedVisitId: "VIS-001",
      linkedServiceId: null,
      convertedTarget: "visit",
      convertedAt: "2026-04-18 10:55:00",
      createdAt: "2026-04-18 09:55:00",
    },
  ];

  const services: VisitServiceRecord[] = [
    {
      id: "SVC-APPT-001",
      visitId: "",
      orderId: null,
      definitionId: "SVC-OBS",
      serviceName: "Observation stay",
      code: "SVC-OBS",
      status: "Ordered",
      priority: "Normal",
      assignedTo: "Dr. Rana Yousif",
      note: "Standalone service created from appointment.",
      price: 15000,
      performedAt: null,
    },
  ];

  appointments.push({
    id: "APT-004",
    customerName: "Zain Kareem",
    customerEmail: "zain@example.com",
    customerPhoneNumber: guardians.zain.phone,
    customerDetails: "Observation service booking",
    petId: pets.coco.id,
    guardianId: guardians.zain.id,
    status: "Closed",
    pet: pets.coco,
    guardian: guardians.zain,
    scheduledTime: "2026-04-19 06:15:00",
    scheduledDate: "2026-04-19",
    scheduledClock: "06:15",
    customAppointmentType: "barbering",
    customPet: pets.coco.id,
    customGuardian: guardians.zain.id,
    customCustomer: "CUST-ZAIN",
    selectedServiceTemplateId: "SVC-OBS",
    selectedServiceTemplateName: "Observation stay",
    doctorId: "doctor@example.com",
    doctorName: "Dr. Rana Yousif",
    linkedVisitId: null,
    linkedServiceId: "SVC-APPT-001",
    convertedTarget: "service",
    convertedAt: "2026-04-19 06:30:00",
    createdAt: "2026-04-18 18:30:00",
  });

  const boarding: BoardingDetail[] = [
    {
      id: "BOARD-001",
      boardingRecordId: asBoardingRecordId("BOARD-001"),
      roomId: asRoomId("ROOM-001")!,
      unitCode: "A-01",
      unitLabel: "Cat Suite 1",
      status: "Occupied",
      recordStatus: "Checked In",
      pet: pets.luna,
      guardian: guardians.sarah,
      reservedAt: "2026-04-17 18:20:00",
      checkIn: "2026-04-18",
      checkOut: "2026-04-21",
      stayDays: 3,
      stayHours: 72,
      totalCost: 70500,
      deposit: 20000,
      balance: 50500,
      note: "Reserved as a calm room assignment.",
      notes: "Needs quiet enclosure.",
      checkoutNotes: null,
      billingStatus: "Pending",
      roomType: "Suite",
      billableItems: [
        {
          id: "BILL-001",
          itemName: "Boarding stay",
          itemType: "service",
          qty: 3,
          rate: 20000,
          amount: 60000,
          note: "Base boarding charge",
          status: "Pending",
        },
        {
          id: "BILL-002",
          itemName: "Evening wet food",
          itemType: "item",
          qty: 3,
          rate: 3500,
          amount: 10500,
          note: "Requested by owner",
          status: "Pending",
        },
      ],
    },
    {
      id: "BOARD-002",
      boardingRecordId: asBoardingRecordId("BOARD-002"),
      roomId: asRoomId("ROOM-003")!,
      unitCode: "B-01",
      unitLabel: "Dog Kennel 1",
      status: "Occupied",
      recordStatus: "Checked In",
      pet: pets.rocky,
      guardian: guardians.omar,
      reservedAt: "2026-04-18 14:05:00",
      checkIn: "2026-04-19",
      checkOut: "2026-04-22",
      stayDays: 3,
      stayHours: 72,
      totalCost: 85000,
      deposit: 25000,
      balance: 60000,
      note: "Reserved for an active medium dog stay.",
      notes: "Walk twice daily.",
      checkoutNotes: null,
      billingStatus: "Pending",
      roomType: "Kennel",
      billableItems: [
        {
          id: "BILL-003",
          itemName: "Boarding stay",
          itemType: "service",
          qty: 3,
          rate: 25000,
          amount: 75000,
          note: "Standard kennel rate",
          status: "Pending",
        },
        {
          id: "BILL-004",
          itemName: "Extra walk",
          itemType: "service",
          qty: 2,
          rate: 5000,
          amount: 10000,
          note: "Afternoon enrichment walk",
          status: "Pending",
        },
      ],
    },
    {
      id: "BOARD-003",
      boardingRecordId: asBoardingRecordId("BOARD-003"),
      roomId: asRoomId("ROOM-004")!,
      unitCode: "B-02",
      unitLabel: "Dog Kennel 2",
      status: "Reserved",
      recordStatus: "Reserved",
      pet: pets.bella,
      guardian: guardians.noor,
      reservedAt: "2026-04-19 09:15:00",
      checkIn: "2026-04-20",
      checkOut: "2026-04-23",
      stayDays: 3,
      stayHours: 72,
      totalCost: null,
      deposit: null,
      balance: null,
      note: "Reserved for tomorrow arrival.",
      notes: null,
      checkoutNotes: null,
      billingStatus: null,
      roomType: "Kennel",
      billableItems: [],
    },
    {
      id: "BOARD-004",
      boardingRecordId: asBoardingRecordId("BOARD-004"),
      roomId: asRoomId("ROOM-005")!,
      unitCode: "R-01",
      unitLabel: "Small Pet Room 1",
      status: "Attention",
      recordStatus: "Checked In",
      pet: pets.coco,
      guardian: guardians.zain,
      reservedAt: "2026-04-19 06:45:00",
      checkIn: "2026-04-19",
      checkOut: "2026-04-20",
      stayDays: 1,
      stayHours: 24,
      totalCost: 23000,
      deposit: 15000,
      balance: 8000,
      note: "Reserved for treatment observation.",
      notes: "Observe appetite closely.",
      checkoutNotes: null,
      billingStatus: "Paid",
      roomType: "Observation",
      billableItems: [
        {
          id: "BILL-005",
          itemName: "Observation stay",
          itemType: "service",
          qty: 1,
          rate: 15000,
          amount: 15000,
          note: "One day observation room stay",
          status: "Paid",
        },
        {
          id: "BILL-006",
          itemName: "Supportive feeding",
          itemType: "service",
          qty: 1,
          rate: 8000,
          amount: 8000,
          note: "Assisted feeding and monitoring",
          status: "Paid",
        },
      ],
    },
    {
      id: "BOARD-005",
      boardingRecordId: asBoardingRecordId("BOARD-005"),
      roomId: asRoomId("ROOM-002")!,
      unitCode: "A-02",
      unitLabel: "Cat Suite 2",
      status: "Available",
      recordStatus: "Checked Out",
      pet: pets.milo,
      guardian: guardians.layla,
      reservedAt: "2026-04-12 11:10:00",
      checkIn: "2026-04-13",
      checkOut: "2026-04-15",
      stayDays: 2,
      stayHours: 48,
      totalCost: 40000,
      deposit: 20000,
      balance: 20000,
      note: "Reserved for a short suite stay.",
      notes: "Completed stay.",
      checkoutNotes: "Picked up on time with no issues.",
      billingStatus: "Paid",
      roomType: "Suite",
      billableItems: [
        {
          id: "BILL-007",
          itemName: "Boarding stay",
          itemType: "service",
          qty: 2,
          rate: 20000,
          amount: 40000,
          note: "Historical completed stay",
          status: "Paid",
        },
      ],
    },
  ];

  return { appointments, caseSheets, visits, services, boarding };
}

function loadDb(): HealthcareMockDb {
  if (typeof window === "undefined")
    return seedDb();

  const seeded = seedDb();
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
    return seeded;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<HealthcareMockDb>;
    return {
      appointments: parsed.appointments ?? seeded.appointments,
      caseSheets: parsed.caseSheets ?? seeded.caseSheets,
      visits: parsed.visits ?? seeded.visits,
      services: parsed.services ?? seeded.services,
      boarding: parsed.boarding ?? seeded.boarding,
    };
  } catch {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
    return seeded;
  }
}

function saveDb(db: HealthcareMockDb) {
  if (typeof window !== "undefined")
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(db));
}

function sortByOrder<T extends Record<string, any>>(items: T[], orderBy?: string | null) {
  const [rawField, rawDirection] = String(orderBy || "").split(" ");
  const field = rawField || "createdAt";
  const direction = (rawDirection || "desc").toLowerCase() === "asc" ? 1 : -1;

  return [...items].sort((left, right) => {
    const a = String(left[field] ?? "");
    const b = String(right[field] ?? "");
    return a.localeCompare(b) * direction;
  });
}

function paginate<T>(items: T[], limitStart = 0, limitPageLength = 10): FrappeListResult<T> {
  return {
    data: items.slice(limitStart, limitStart + limitPageLength),
    total: items.length,
  };
}

function includesQuery(values: Array<string | null | undefined>, query?: string) {
  if (!query)
    return true;

  const normalized = query.trim().toLowerCase();
  if (!normalized)
    return true;

  return values.some(value => String(value || "").toLowerCase().includes(normalized));
}

interface MockPetProfileState {
  pets: Pet[];
  guardians: Guardian[];
  guardianLinks: PetGuardian[];
}

type MockPetFilterInput = {
  search?: string;
  animal_species?: string;
  animal_type?: string;
  status?: string;
  filters?: string;
  or_filters?: string;
  order_by?: string;
  limit_start?: number;
  limit_page_length?: number;
};

function seedPetProfileState(): MockPetProfileState {
  const pets: Pet[] = [
    {
      name: "PET-001",
      pet_name: "Luna",
      animal_species: AnimalSpecies.Mammal,
      animal_type: AnimalType.Cat,
      breed: "British Shorthair",
      birth_date: "2023-02-14",
      registration_date: "2025-11-03",
      pet_image: null,
      image: "",
      color: "Silver",
      gender: AnimalGender.Female,
      weight: 4.2,
      hight: 24,
      blood_type: BloodType.APositive,
      play: "Feather wand",
      activity_exercise: "Indoor play twice daily",
      status: PetStatus.Available,
      food_type: "Dry food",
      type_name: "Dry food",
      food_brand: "Royal Canin Indoor",
      brand_name: "Royal Canin Indoor",
      description: "Sensitive stomach history with one previous gastritis visit.",
      note: "Prefers quiet rooms and slow handling.",
      images: [],
    },
    {
      name: "PET-002",
      pet_name: "Rocky",
      animal_species: AnimalSpecies.Mammal,
      animal_type: AnimalType.Dog,
      breed: "Golden Retriever",
      birth_date: "2022-01-09",
      registration_date: "2025-10-18",
      pet_image: null,
      image: "",
      color: "Golden",
      gender: AnimalGender.Male,
      weight: 28.4,
      hight: 58,
      blood_type: BloodType.OPositive,
      play: "Fetch",
      activity_exercise: "Daily walks, currently restricted during treatment",
      status: PetStatus.UnderTreatment,
      food_type: "Joint support diet",
      type_name: "Joint support diet",
      food_brand: "Royal Canin Mobility",
      brand_name: "Royal Canin Mobility",
      description: "Active mock patient for the visit workbench and medical profile test flow.",
      note: "Current active case: left rear limb lameness and pain management.",
      images: [],
    },
    {
      name: "PET-003",
      pet_name: "Milo",
      animal_species: AnimalSpecies.Mammal,
      animal_type: AnimalType.Cat,
      breed: "Siamese",
      birth_date: "2024-03-22",
      registration_date: "2025-12-01",
      pet_image: null,
      image: "",
      color: "Cream point",
      gender: AnimalGender.Male,
      weight: 4.9,
      hight: 26,
      blood_type: BloodType.BPositive,
      play: "Climbing tower",
      activity_exercise: "Moderate indoor activity",
      status: PetStatus.Deceased,
      food_type: "Wet food",
      type_name: "Wet food",
      food_brand: "Hill's Sensitive",
      brand_name: "Hill's Sensitive",
      description: "Mock deceased patient used by the Graveyard module.",
      note: "Mortality mock data includes linked death record PDR-2026-00003.",
      images: [],
    },
    {
      name: "PET-004",
      pet_name: "Bella",
      animal_species: AnimalSpecies.Mammal,
      animal_type: AnimalType.Dog,
      breed: "Pomeranian",
      birth_date: "2021-08-17",
      registration_date: "2025-09-12",
      pet_image: null,
      image: "",
      color: "White",
      gender: AnimalGender.Female,
      weight: 3.6,
      hight: 22,
      blood_type: BloodType.APositive,
      play: "Soft ball",
      activity_exercise: "Short walks",
      status: PetStatus.Available,
      food_type: "Small breed kibble",
      type_name: "Small breed kibble",
      food_brand: "Purina Pro Plan",
      brand_name: "Purina Pro Plan",
      description: "Dental follow-up mock patient.",
      note: "Recently completed post-op dental check.",
      images: [],
    },
    {
      name: "PET-005",
      pet_name: "Coco",
      animal_species: AnimalSpecies.Mammal,
      animal_type: AnimalType.Rabbit,
      breed: "Mini Lop",
      birth_date: "2025-04-11",
      registration_date: "2026-01-05",
      pet_image: null,
      image: "",
      color: "Brown",
      gender: AnimalGender.Female,
      weight: 1.8,
      hight: 18,
      blood_type: BloodType.APositive,
      play: "Chew toys",
      activity_exercise: "Supervised room time",
      status: PetStatus.Available,
      food_type: "Hay and pellets",
      type_name: "Hay and pellets",
      food_brand: "Oxbow Essentials",
      brand_name: "Oxbow Essentials",
      description: "Boarding mock patient for coordinator and appointment tests.",
      note: "Handle gently; anxious around loud noise.",
      images: [],
    },
    {
      name: "PET-006",
      pet_name: "Nala",
      animal_species: AnimalSpecies.Mammal,
      animal_type: AnimalType.Cat,
      breed: "Domestic Shorthair",
      birth_date: "2024-06-10",
      registration_date: "2026-02-12",
      pet_image: null,
      image: "",
      color: "Calico",
      gender: AnimalGender.Female,
      weight: 3.9,
      hight: 25,
      blood_type: BloodType.APositive,
      play: "Ribbon toy",
      activity_exercise: "Indoor play",
      status: PetStatus.Available,
      food_type: "Dry food",
      type_name: "Dry food",
      food_brand: "Purina One",
      brand_name: "Purina One",
      description: "Mock pet without a linked guardian for empty-state testing.",
      note: "Link a guardian before using contact actions.",
      images: [],
    },
  ];

  const guardians: Guardian[] = [
    {
      name: "GUARD-001",
      full_name: "Sarah Ahmed",
      image: "",
      guardian_image: "",
      email_id: "sarah@example.com",
      phone: "07700000001",
      is_active: true,
      customer_id: "CUST-001",
      address_line1: "Baghdad, Street 14",
      city: "Baghdad",
      country: "Iraq",
      creation: "2025-11-03 09:00:00",
      modified: "2025-11-03 09:00:00",
    },
    {
      name: "GUARD-002",
      full_name: "Omar Hassan",
      image: "",
      guardian_image: "",
      email_id: "omar@example.com",
      phone: "07700000002",
      is_active: true,
      customer_id: "CUST-002",
      address_line1: "Baghdad",
      city: "Baghdad",
      country: "Iraq",
      creation: "2025-10-18 10:20:00",
      modified: "2025-10-18 10:20:00",
    },
    {
      name: "GUARD-003",
      full_name: "Layla Kareem",
      image: "",
      guardian_image: "",
      email_id: "layla@example.com",
      phone: "07700000003",
      is_active: true,
      customer_id: "CUST-003",
      address_line1: "Baghdad",
      city: "Baghdad",
      country: "Iraq",
      creation: "2025-12-01 13:15:00",
      modified: "2025-12-01 13:15:00",
    },
    {
      name: "GUARD-004",
      full_name: "Noor Ali",
      image: "",
      guardian_image: "",
      email_id: "noor@example.com",
      phone: "07700000004",
      is_active: true,
      customer_id: "CUST-004",
      address_line1: "Baghdad",
      city: "Baghdad",
      country: "Iraq",
      creation: "2025-09-12 15:30:00",
      modified: "2025-09-12 15:30:00",
    },
    {
      name: "GUARD-005",
      full_name: "Zain Abbas",
      image: "",
      guardian_image: "",
      email_id: "zain@example.com",
      phone: "07700000005",
      is_active: true,
      customer_id: "CUST-005",
      address_line1: "Baghdad",
      city: "Baghdad",
      country: "Iraq",
      creation: "2026-01-05 11:45:00",
      modified: "2026-01-05 11:45:00",
    },
  ];

  const linkSpecs = [
    ["PG-001", "GUARD-001", "PET-001"],
    ["PG-006", "GUARD-002", "PET-001"],
    ["PG-002", "GUARD-002", "PET-002"],
    ["PG-003", "GUARD-003", "PET-003"],
    ["PG-004", "GUARD-004", "PET-004"],
    ["PG-005", "GUARD-005", "PET-005"],
  ] as const;

  const guardianLinks: PetGuardian[] = linkSpecs.flatMap(([name, guardianId, petId]) => {
    const guardian = guardians.find(item => item.name === guardianId);
    const pet = pets.find(item => item.name === petId);

    if (!guardian || !pet)
      return [];

    return [
      {
        name,
        guardian_id: guardian.name,
        full_name: guardian.full_name,
        guardian_image: guardian.image || "",
        phone: guardian.phone || null,
        customer_id: guardian.customer_id || null,
        pet_id: String(pet.name),
        pet_name: pet.pet_name,
        pet_image: pet.pet_image || "",
        status: "Active",
        creation: guardian.creation,
      },
    ];
  });

  return { pets, guardians, guardianLinks };
}

function loadPetProfileState(): MockPetProfileState {
  const seeded = seedPetProfileState();

  if (typeof window === "undefined")
    return seeded;

  const raw = window.localStorage.getItem(PET_PROFILE_STORAGE_KEY);
  if (!raw) {
    window.localStorage.setItem(PET_PROFILE_STORAGE_KEY, JSON.stringify(seeded));
    return seeded;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<MockPetProfileState>;

    return {
      pets: parsed.pets?.length ? parsed.pets : seeded.pets,
      guardians: parsed.guardians?.length ? parsed.guardians : seeded.guardians,
      guardianLinks: parsed.guardianLinks ?? seeded.guardianLinks,
    };
  } catch {
    window.localStorage.setItem(PET_PROFILE_STORAGE_KEY, JSON.stringify(seeded));
    return seeded;
  }
}

function savePetProfileState(state: MockPetProfileState) {
  if (typeof window !== "undefined")
    window.localStorage.setItem(PET_PROFILE_STORAGE_KEY, JSON.stringify(state));
}

function parseMockFilterClauses(value?: string): FrappeFilterClause[] {
  if (!value)
    return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed as FrappeFilterClause[] : [];
  } catch {
    return [];
  }
}

function mockFilterParts(clause: FrappeFilterClause) {
  if (!Array.isArray(clause))
    return null;

  if (clause.length === 4)
    return { field: String(clause[1]), operator: String(clause[2]), value: clause[3] };

  if (clause.length === 3)
    return { field: String(clause[0]), operator: String(clause[1]), value: clause[2] };

  return null;
}

function readMockFieldValue(item: Record<string, unknown>, field: string) {
  const direct = item[field];
  if (direct !== undefined)
    return direct;

  return field.split(".").reduce<unknown>((current, segment) => {
    if (current && typeof current === "object")
      return (current as Record<string, unknown>)[segment];

    return undefined;
  }, item);
}

function compareMockField(item: Record<string, unknown>, clause: FrappeFilterClause) {
  const parts = mockFilterParts(clause);
  if (!parts)
    return true;

  const actual = readMockFieldValue(item, parts.field);
  const operator = parts.operator.toLowerCase();
  const expected = parts.value;
  const actualText = String(actual ?? "").toLowerCase();
  const expectedText = String(expected ?? "").replace(/%/g, "").toLowerCase();

  if (operator === "like")
    return actualText.includes(expectedText);

  if (operator === "not like")
    return !actualText.includes(expectedText);

  if (operator === "in" && Array.isArray(expected))
    return expected.map(value => String(value).toLowerCase()).includes(actualText);

  if (operator === "not in" && Array.isArray(expected))
    return !expected.map(value => String(value).toLowerCase()).includes(actualText);

  if (operator === "!=")
    return actualText !== expectedText;

  return actualText === expectedText;
}

function applyMockListFilters<T extends object>(
  items: T[],
  filters?: string,
  orFilters?: string,
) {
  const parsedFilters = parseMockFilterClauses(filters);
  const parsedOrFilters = parseMockFilterClauses(orFilters);

  return items.filter((item) => {
    const itemRecord = item as Record<string, unknown>;
    const matchesFilters = parsedFilters.every(clause => compareMockField(itemRecord, clause));
    const matchesOrFilters = parsedOrFilters.length
      ? parsedOrFilters.some(clause => compareMockField(itemRecord, clause))
      : true;

    return matchesFilters && matchesOrFilters;
  });
}

function sortMockPets(pets: Pet[]) {
  return [...pets].sort((left, right) =>
    String(left.pet_name || left.name || "").localeCompare(String(right.pet_name || right.name || "")),
  );
}

function nextMockPetName(pets: Pet[]) {
  const count = pets.filter(item => String(item.name || "").startsWith("PET-MOCK-")).length + 1;
  return `PET-MOCK-${String(count).padStart(3, "0")}`;
}

function nextMockGuardianName(guardians: Guardian[]) {
  const count = guardians.filter(item => String(item.name || "").startsWith("GUARD-MOCK-")).length + 1;
  return `GUARD-MOCK-${String(count).padStart(3, "0")}`;
}

function normalizeMockPetPayload(payload: AddEditPet, existing?: Pet | null): Pet {
  const petName = payload.name || existing?.name || "";

  return {
    name: petName,
    pet_name: payload.pet_name || existing?.pet_name || "Mock Pet",
    animal_species: payload.animal_species || existing?.animal_species || AnimalSpecies.Mammal,
    animal_type: payload.animal_type || existing?.animal_type || AnimalType.Dog,
    breed: payload.breed || existing?.breed || "",
    birth_date: payload.birth_date || existing?.birth_date || "",
    registration_date: payload.registration_date || existing?.registration_date || getNowIso().slice(0, 10),
    description: payload.description ?? existing?.description ?? "",
    note: existing?.note ?? "",
    pet_image: payload.image || existing?.pet_image || null,
    image: payload.image || existing?.image || "",
    color: payload.color || existing?.color || "",
    gender: payload.gender || existing?.gender || AnimalGender.Unknown,
    weight: payload.weight ?? existing?.weight ?? "",
    hight: payload.hight ?? existing?.hight ?? "",
    blood_type: payload.blood_type || existing?.blood_type || BloodType.APositive,
    play: payload.play ?? existing?.play ?? "",
    activity_exercise: payload.activity_exercise || existing?.activity_exercise || "",
    status: payload.status || existing?.status || PetStatus.Available,
    food_type: payload.food_type || existing?.food_type || "",
    type_name: existing?.type_name || payload.food_type || "",
    food_brand: payload.food_brand || existing?.food_brand || "",
    brand_name: existing?.brand_name || payload.food_brand || "",
    images: existing?.images || [],
  };
}

export async function mockGetPets(filter: MockPetFilterInput = {}): Promise<{ data: Pet[]; total: number }> {
  const state = loadPetProfileState();
  const searched = state.pets.filter(item =>
    includesQuery([
      String(item.name ?? ""),
      item.pet_name,
      item.animal_species,
      item.animal_type,
      item.breed,
      item.color,
      item.status,
    ], filter.search),
  );

  const explicitlyFiltered = searched.filter(item =>
    sameOrEmpty(String(item.animal_species || ""), filter.animal_species)
    && sameOrEmpty(String(item.animal_type || ""), filter.animal_type)
    && sameOrEmpty(String(item.status || ""), filter.status),
  );

  const filtered = applyMockListFilters(
    explicitlyFiltered,
    filter.filters,
    filter.or_filters,
  );

  const start = Number(filter.limit_start ?? 0);
  const length = Number(filter.limit_page_length ?? (filtered.length || 10));

  return withDelay({
    data: sortMockPets(filtered).slice(start, start + length),
    total: filtered.length,
  });
}

export async function mockGetPetById(id: string | number): Promise<Pet | null> {
  const state = loadPetProfileState();
  const pet = state.pets.find(item => String(item.name) === String(id)) || null;
  return withDelay(pet);
}

export async function mockAddPet(payload: AddEditPet): Promise<string | null> {
  const state = loadPetProfileState();
  const name = payload.name || nextMockPetName(state.pets);
  const created = normalizeMockPetPayload({ ...payload, name }, null);

  state.pets = [created, ...state.pets];
  savePetProfileState(state);

  return withDelay(String(created.name || ""));
}

export async function mockLinkPetToGuardian(guardianId: string, petId: string): Promise<boolean> {
  const state = loadPetProfileState();
  const guardian = state.guardians.find(item => String(item.name) === String(guardianId));
  const pet = state.pets.find(item => String(item.name) === String(petId));

  if (!guardian || !pet)
    return withDelay(false);

  const exists = state.guardianLinks.some(link =>
    String(link.guardian_id) === String(guardianId)
    && String(link.pet_id) === String(petId),
  );

  if (exists)
    return withDelay(true);

  state.guardianLinks = [
    {
      name: `PG-MOCK-${Date.now()}`,
      guardian_id: guardian.name,
      full_name: guardian.full_name,
      guardian_image: guardian.image || "",
      pet_id: String(pet.name),
      pet_name: pet.pet_name,
      pet_image: pet.pet_image || "",
      status: "Active",
      creation: new Date().toISOString(),
    },
    ...state.guardianLinks,
  ];
  savePetProfileState(state);

  return withDelay(true);
}

export async function mockUpdatePet(payload: AddEditPet): Promise<Pet | null> {
  if (!payload.name)
    return withDelay(null);

  const state = loadPetProfileState();
  const index = state.pets.findIndex(item => String(item.name) === String(payload.name));
  if (index === -1)
    return withDelay(null);

  const updated = normalizeMockPetPayload(payload, state.pets[index]);
  state.pets[index] = updated;

  state.guardianLinks = state.guardianLinks.map(link =>
    String(link.pet_id) === String(updated.name)
      ? {
          ...link,
          pet_name: updated.pet_name,
          pet_image: updated.pet_image || "",
        }
      : link,
  );

  savePetProfileState(state);
  return withDelay(updated);
}

export async function mockDeletePet(id: string | number): Promise<boolean> {
  const state = loadPetProfileState();
  const before = state.pets.length;
  state.pets = state.pets.filter(item => String(item.name) !== String(id));
  state.guardianLinks = state.guardianLinks.filter(link => String(link.pet_id) !== String(id));
  savePetProfileState(state);

  return withDelay(state.pets.length !== before);
}

export async function mockGetPetsByGuardian(guardianId: string): Promise<PetDropDown[]> {
  const state = loadPetProfileState();

  const rows = state.guardianLinks
    .filter(link => String(link.guardian_id) === String(guardianId))
    .map<PetDropDown>(link => ({
      pet_id: String(link.pet_id),
      pet_name: link.pet_name,
      pet_image: link.pet_image,
    }));

  return withDelay(rows);
}

export async function mockGetPetGuardians(petId: string): Promise<PetGuardian[]> {
  const state = loadPetProfileState();
  const rows = state.guardianLinks
    .filter(link => String(link.pet_id) === String(petId))
    .map((link) => {
      const guardian = state.guardians.find(item => item.name === link.guardian_id);
      return {
        ...link,
        phone: link.phone ?? guardian?.phone ?? null,
        customer_id: link.customer_id ?? guardian?.customer_id ?? null,
      };
    });
  return withDelay(rows);
}

function filterMockGuardians(guardians: Guardian[], filter: MockPetFilterInput = {}) {
  const searched = guardians.filter(item =>
    includesQuery([item.name, item.full_name, item.phone, item.email_id], filter.search),
  );

  return applyMockListFilters(
    searched,
    filter.filters,
    filter.or_filters,
  );
}

export async function mockGetGuardianDropDown(filter: MockPetFilterInput = {}): Promise<{ data: GuardianDropDown[] }> {
  const state = loadPetProfileState();
  const filtered = filterMockGuardians(state.guardians, filter);
  const start = Number(filter.limit_start ?? 0);
  const length = Number(filter.limit_page_length ?? (filtered.length || 10));

  return withDelay({
    data: filtered.slice(start, start + length).map(item => ({
      name: item.name,
      full_name: item.full_name,
      image: item.image,
    })),
  });
}

export async function mockGetGuardians(filter: MockPetFilterInput = {}): Promise<FrappeListResult<Guardian>> {
  const state = loadPetProfileState();
  const filtered = sortByOrder(
    filterMockGuardians(state.guardians, filter) as unknown as Record<string, any>[],
    filter.order_by || "modified desc",
  ) as Guardian[];
  const start = Number(filter.limit_start ?? 0);
  const length = Number(filter.limit_page_length ?? (filtered.length || 10));

  return withDelay({
    data: filtered.slice(start, start + length),
    total: filtered.length,
  });
}

export async function mockCreateGuardian(payload: CreateGuardianPayload): Promise<Guardian | null> {
  const state = loadPetProfileState();
  const now = new Date().toISOString();
  const name = nextMockGuardianName(state.guardians);
  const created: Guardian = {
    name,
    full_name: payload.fullName.trim(),
    image: payload.guardianImage ?? null,
    guardian_image: payload.guardianImage ?? null,
    email_id: payload.email?.trim() || null,
    phone: payload.phone?.trim() || "",
    is_active: false,
    customer_id: payload.fullName.trim(),
    teritory: payload.territory?.trim() || null,
    address_line1: payload.addressLine1?.trim() || null,
    city: payload.city?.trim() || null,
    country: payload.country?.trim() || "Iraq",
    creation: now,
    modified: now,
  };

  state.guardians = [created, ...state.guardians];
  savePetProfileState(state);

  return withDelay(created);
}

export async function mockGetGuardianProfile(guardianId: string): Promise<GuardianProfileData | null> {
  const state = loadPetProfileState();
  const guardian = state.guardians.find(item => String(item.name) === String(guardianId));

  if (!guardian)
    return withDelay(null);

  return withDelay({
    guardian: {
      id: guardian.name,
      phone: guardian.phone,
      full_name: guardian.full_name,
      image: guardian.guardian_image || guardian.image || null,
      guardian_image: guardian.guardian_image || guardian.image || null,
      is_active: guardian.is_active ? 1 : 0,
      otp_verified: 1,
      user_id: `${guardian.name.toLowerCase()}@example.com`,
      customer_id: guardian.customer_id || `CUST-${guardian.name.replace("GUARD-", "")}`,
      joined: guardian.creation,
    },
    customer: {
      id: guardian.customer_id || `CUST-${guardian.name.replace("GUARD-", "")}`,
      customer_name: guardian.full_name,
      mobile_no: guardian.phone,
      email_id: guardian.email_id,
      orders_count: 3,
      total_spent: 175000,
    },
    addresses: [
      {
        id: `ADDR-${guardian.name.replace("GUARD-", "")}`,
        title: "Home",
        type: "Billing",
        address_line1: guardian.address_line1 || "Mock clinic test address",
        address_line2: null,
        city: guardian.city || "Baghdad",
        country: guardian.country || "Iraq",
        is_primary: true,
      },
    ],
  });
}

function sameOrEmpty(value: string | null | undefined, target?: string | null) {
  if (!target)
    return true;

  return String(value || "").toLowerCase() === String(target).toLowerCase();
}

function matchesDateRange(value?: string | null, from?: string | null, to?: string | null) {
  if (!value)
    return !from && !to;

  const day = String(value).slice(0, 10);

  if (from && day < from)
    return false;
  if (to && day > to)
    return false;

  return true;
}

function deriveOccupancyFromRecordStatus(
  recordStatus?: BoardingRecordStatus | null,
  fallback?: BoardingOccupancyStatus | null,
): BoardingOccupancyStatus {
  const normalized = String(recordStatus || "").toLowerCase();

  if (normalized === "reserved")
    return "Reserved";
  if (normalized === "checked in")
    return fallback || "Occupied";

  return fallback || "Available";
}

function isActiveBoardingRecord(record?: BoardingDetail | null) {
  const normalized = String(record?.recordStatus || "").toLowerCase();
  return !["checked out", "cancelled"].includes(normalized);
}

function buildBoardingStayDays(checkIn?: string | null, checkOut?: string | null) {
  if (!checkIn || !checkOut)
    return null;

  const start = new Date(checkIn);
  const end = new Date(checkOut);
  const diff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

  return Number.isFinite(diff) ? Math.max(diff, 0) : null;
}

function buildBoardingStayHours(checkIn?: string | null, checkOut?: string | null) {
  if (!checkIn || !checkOut)
    return null;

  const start = new Date(checkIn);
  const end = new Date(checkOut);
  const diff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60));

  // Mirror the backend contract: whole started hours, floor of 1.
  return Number.isFinite(diff) ? Math.max(diff, 1) : null;
}

function sumBillableItems(items?: BoardingDetail["billableItems"]) {
  const total = (items || []).reduce((sum, item) => {
    const amount = Number(item.amount ?? 0);
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);

  return total > 0 ? total : null;
}

/**
 * Price boarding billable lines the way the BACKEND does.
 *
 * The client now sends only `item_code` + `qty` — no rate, no amount (BOARDING_GAPS Tier-1 #2).
 * So the mock has to be the pricing authority, exactly like the real server: it looks the rate
 * up from the row already stored against this stay (`existing`), and only falls back to a
 * nominal rate for a genuinely new line. If this function simply echoed a client-sent rate,
 * mock mode would silently keep passing where the real backend now prices — hiding the bug
 * the fix exists to prevent.
 */
const MOCK_DEFAULT_BILLABLE_RATE = 10000;

function normalizeMockBillableItems(
  items?: BoardingBillableItemWrite[],
  existing?: BoardingDetail["billableItems"],
) {
  return (items || []).map((item, index) => {
    const qty = Number(item.qty ?? 0);
    const safeQty = Number.isFinite(qty) ? qty : 0;

    // Match the stored row by child-row name first, then by item code.
    const previous = (existing || []).find(row =>
      (item.name && String(row.id) === String(item.name))
      || (item.item_code && row.itemCode === item.item_code),
    );
    const rate = Number(previous?.rate ?? MOCK_DEFAULT_BILLABLE_RATE);
    const safeRate = Number.isFinite(rate) ? rate : 0;

    return {
      id: item.name || previous?.id || `BILL-${index + 1}`,
      itemName: item.item_name || previous?.itemName || "",
      itemCode: item.item_code ?? previous?.itemCode ?? null,
      itemType: item.item_type ?? previous?.itemType ?? null,
      linkedServiceId: item.linked_service_id ?? previous?.linkedServiceId ?? null,
      qty: safeQty,
      rate: safeRate,
      amount: safeQty * safeRate,
      note: item.note ?? null,
    };
  });
}

function syncBoardingBilling(record: BoardingDetail) {
  // `balance` is DELIBERATELY not written here.
  //
  // It used to be computed as `totalCost - deposit`, which is not a figure the invoice honours:
  // invoice reuse means one invoice can carry several stays' charges, so the outstanding is the
  // CUSTOMER's, not this stay's. The backend has relabelled its own field an estimate for the
  // same reason. The real path reads `balance` straight through from the backend (see
  // `mapBoardingDetail` in healthcareApi.utils.ts) and computes nothing — a mock that keeps
  // manufacturing the number would go on validating behaviour the backend no longer has.
  record.totalCost = sumBillableItems(record.billableItems);
}

function buildVisitHistory(db: HealthcareMockDb, petId?: string | null, currentVisitId?: string | null) {
  return db.visits
    .filter(item => item.pet.id === petId && item.id !== currentVisitId)
    .map<VisitHistoryItem>(item => ({
      id: item.id,
      title: item.caseSheetNo || item.id,
      subtitle: item.status,
      date: item.visitDate,
      time: item.visitTime,
      practitioner: item.practitionerName,
      note: item.chiefComplaint,
      priority: item.priority,
      billingStatus: item.billing.status,
      orderCount: item.orders.length,
      assessment: item.assessment,
      plan: item.plan,
      total: item.billing.total,
      currency: item.billing.currency,
    }));
}

interface MockWorkbenchState {
  vitals: Record<string, Record<string, unknown>>;
  vitalHistory: Record<string, Array<Record<string, unknown>>>;
  diagnoses: Record<string, Array<Record<string, unknown>>>;
  medications: Record<string, Array<Record<string, unknown>>>;
  clinicalNotes: Record<string, Record<string, unknown>>;
  planItems: Record<string, Array<Record<string, unknown>>>;
  ownerInstructions: Record<string, string>;
  caseChoices: Record<string, "wellness" | "continue_case" | "new_case" | null>;
  /** Raw `consultation_timer` blocks by visit id — see utils/visitConsultationTimer.ts. */
  consultationTimers: Record<string, Record<string, unknown>>;
}

function seedWorkbenchState(): MockWorkbenchState {
  return {
    vitals: {},
    medications: {},
    clinicalNotes: {},
    vitalHistory: {
      "VIS-001": [
        {
          recorded_at: "2026-04-14 09:10:00",
          visit: "VIS-003",
          episode: "PCE-ROCKY-LIMP",
          temperature: 38.4,
          heart_rate: 92,
          respiratory_rate: 24,
          weight: 28.1,
          spo2: 98,
          pain_score: 1,
          note: "Baseline wellness values before the injury episode.",
        },
        {
          recorded_at: "2026-04-18 10:58:00",
          visit: "VIS-001",
          episode: "PCE-ROCKY-LIMP",
          temperature: 38.8,
          heart_rate: 118,
          respiratory_rate: 32,
          weight: 28.4,
          spo2: 97,
          pain_score: 5,
          note: "Pain response during limb manipulation.",
        },
        {
          recorded_at: "2026-04-19 09:40:00",
          visit: "VIS-001",
          episode: "PCE-ROCKY-LIMP",
          temperature: 38.6,
          heart_rate: 104,
          respiratory_rate: 28,
          weight: 28.2,
          spo2: 98,
          pain_score: 3,
          note: "Improved after rest and medication.",
        },
      ],
    },
    diagnoses: {
      "VIS-001": [
        {
          name: "DX-ROCKY-001",
          disease: "Soft tissue strain",
          diagnosis_text: "Rear left limb soft tissue strain; rule out cruciate ligament injury if lameness persists.",
          is_primary: 1,
          severity: "Important",
          note: "Pain on extension, no open wound.",
        },
      ],
    },
    planItems: {
      "VIS-001": [
        {
          name: "PLAN-ROCKY-001",
          title: "Carprofen course",
          item_type: "Medication",
          status: "In Progress",
          priority: "Important",
          due_date: "2026-04-18",
          requires_appointment: 0,
          requires_reminder: 0,
          owner_instructions: "Give with food and stop if vomiting occurs.",
          medication: "Carprofen",
          dose: "2 mg/kg",
          frequency: "BID",
          duration: "5 days",
          instructions: "Dispense from clinic.",
          care_episode: "PCE-ROCKY-LIMP",
          doctor: "doctor@example.com",
          pet: "PET-002",
          guardian: "GUARD-002",
          visit: "VIS-001",
        },
        {
          name: "PLAN-ROCKY-002",
          title: "Lameness recheck",
          item_type: "Follow-up",
          status: "Needs Appointment",
          priority: "Normal",
          due_date: "2026-04-23",
          requires_appointment: 1,
          requires_reminder: 1,
          owner_instructions: "Return sooner if Rocky cannot bear weight.",
          care_episode: "PCE-ROCKY-LIMP",
          doctor: "doctor@example.com",
          pet: "PET-002",
          guardian: "GUARD-002",
          visit: "VIS-001",
        },
        {
          name: "PLAN-ROCKY-003",
          title: "Activity restriction",
          item_type: "Owner Instruction",
          status: "Planned",
          priority: "Normal",
          due_date: "2026-04-18",
          requires_appointment: 0,
          requires_reminder: 0,
          owner_instructions: "No stairs, jumping, or running for 5 days.",
          care_episode: "PCE-ROCKY-LIMP",
          doctor: "doctor@example.com",
          pet: "PET-002",
          guardian: "GUARD-002",
          visit: "VIS-001",
        },
      ],
    },
    ownerInstructions: {
      "VIS-001": "Keep Rocky rested in a small area. Give medication with food. Call the clinic if pain worsens, appetite drops, or vomiting starts.",
    },
    caseChoices: {
      "VIS-001": "continue_case",
    },
    consultationTimers: {},
  };
}

function loadWorkbenchState(): MockWorkbenchState {
  const seeded = seedWorkbenchState();

  if (typeof window === "undefined")
    return seeded;

  const raw = window.localStorage.getItem(WORKBENCH_STORAGE_KEY);
  if (!raw) {
    window.localStorage.setItem(WORKBENCH_STORAGE_KEY, JSON.stringify(seeded));
    return seeded;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<MockWorkbenchState>;

    return {
      vitals: parsed.vitals ?? seeded.vitals,
      vitalHistory: parsed.vitalHistory ?? seeded.vitalHistory,
      diagnoses: parsed.diagnoses ?? seeded.diagnoses,
      medications: parsed.medications ?? seeded.medications,
      clinicalNotes: parsed.clinicalNotes ?? seeded.clinicalNotes,
      planItems: parsed.planItems ?? seeded.planItems,
      ownerInstructions: parsed.ownerInstructions ?? seeded.ownerInstructions,
      caseChoices: parsed.caseChoices ?? seeded.caseChoices,
      consultationTimers: parsed.consultationTimers ?? seeded.consultationTimers,
    };
  } catch {
    window.localStorage.setItem(WORKBENCH_STORAGE_KEY, JSON.stringify(seeded));
    return seeded;
  }
}

function saveWorkbenchState(state: MockWorkbenchState) {
  if (typeof window !== "undefined")
    window.localStorage.setItem(WORKBENCH_STORAGE_KEY, JSON.stringify(state));
}

function getWorkbenchVisit(db: HealthcareMockDb, visitName: string) {
  return findVisit(db, visitName)
    || findVisit(db, "VIS-001")
    || db.visits[0]
    || null;
}

function latestVitalsForVisit(state: MockWorkbenchState, visitId: string) {
  const explicit = state.vitals[visitId];
  if (explicit)
    return explicit;

  const history = state.vitalHistory[visitId] || [];

  return history[history.length - 1] || {
    recorded_at: "2026-04-18 10:58:00",
    temperature: 38.5,
    heart_rate: 96,
    respiratory_rate: 24,
    weight: 12.4,
    spo2: 98,
    pain_score: 2,
    note: "Mock vitals snapshot.",
  };
}

function buildMockEpisode(visit: VisitDetail) {
  return {
    name: `PCE-${visit.pet.id}-ACTIVE`,
    title: `${visit.pet.name} active medical case`,
    status: visit.status === "Closed" ? "Resolved" : "Under Treatment",
    priority: visit.priority || "Normal",
    diagnosis: "Active orthopedic care plan",
    started_on: visit.startedAt || visit.visitDate,
    next_due_on: "2026-04-23",
    summary: "Mock active case continuing from this Vet Visit with medication, monitoring, and follow-up planning.",
  };
}

function buildMockCaseContext(visit: VisitDetail, state: MockWorkbenchState) {
  const doctorCaseChoice = state.caseChoices[visit.id] ?? null;
  const episode = doctorCaseChoice === "wellness" ? null : buildMockEpisode(visit);
  const profileActiveEpisode = episode?.name ?? null;

  return {
    doctor_case_choice: doctorCaseChoice,
    case_choice_required: !doctorCaseChoice,
    visit_care_episode: doctorCaseChoice === "continue_case" || doctorCaseChoice === "new_case"
      ? episode?.name ?? null
      : null,
    profile_active_episode: profileActiveEpisode,
    active_episode: episode,
    episode_status: episode?.status ?? null,
    can_continue_case: Boolean(profileActiveEpisode),
    can_open_new_case: !profileActiveEpisode,
  };
}

function mockLinkedDoctype(kind?: string | null) {
  const normalized = String(kind || "").toLowerCase();

  if (normalized === "lab")
    return "Lab";
  if (normalized === "radiology")
    return "Imaging";
  if (normalized === "service")
    return "PetCareService";
  if (normalized === "procedure")
    return "Pet Procedure";
  if (normalized === "medication")
    return "Medication Dispense";

  return null;
}

function mockLinkedName(kind?: string | null, index = 0) {
  const doctype = mockLinkedDoctype(kind);
  if (!doctype)
    return null;

  const prefix = doctype
    .replace(/\s+/g, "-")
    .replace("PetCareService", "PCS")
    .replace("Medication-Dispense", "MED-DISP")
    .toUpperCase();

  return `${prefix}-MOCK-${Date.now()}-${index + 1}`;
}

function mockLinkedRecordsFromOrders(orders: VisitOrderItem[] = []) {
  return orders
    .filter(order => order.linkedName || mockLinkedDoctype(order.kind))
    .map((order, index) => {
      const linkedDoctype = order.linkedDoctype || mockLinkedDoctype(order.kind);
      const linkedName = order.linkedName || mockLinkedName(order.kind, index);

      return {
        id: linkedName,
        name: linkedName,
        kind: order.kind,
        source_type: order.kind,
        title: order.title,
        status: order.status || "Ordered",
        priority: order.priority || "Normal",
        summary: order.note || order.instructions || null,
        linked_doctype: linkedDoctype,
        linked_name: linkedName,
      };
    });
}

function buildMockCaseSheet(visit: VisitDetail) {
  return {
    name: visit.caseSheetId || visit.caseSheetNo || "VCS-MOCK",
    chief_complaint: visit.chiefComplaint || "Mock chief complaint",
    intake_summary: visit.intakeSummary || "Mock intake summary for frontend testing.",
    triage_notes: visit.linkedCaseSheet?.triageNotes || "Stable at intake. Doctor review requested.",
    priority: visit.priority || "Normal",
    status: visit.linkedCaseSheet?.status || "In Consultation",
  };
}

function defaultDiagnosisForVisit(visit: VisitDetail) {
  if (!visit.assessment)
    return [];

  return [
    {
      name: `DX-${visit.id}`,
      diagnosis_text: visit.assessment,
      is_primary: 1,
      severity: visit.priority || "Normal",
      note: "Mock diagnosis generated from visit assessment.",
    },
  ];
}

function completionChecklist(workbench: any) {
  return {
    diagnosis_added: Boolean(workbench.diagnoses?.length),
    plan_decided: Boolean(workbench.plan_items?.length),
    owner_instructions_added: Boolean(String(workbench.owner_instructions || "").trim()),
    pending_orders_visible: true,
    billables_visible: Boolean(workbench.billing || workbench.orders?.length),
  };
}

export async function mockGetVisitWorkbench(visitName: string): Promise<any> {
  const db = loadDb();
  const state = loadWorkbenchState();
  const visit = getWorkbenchVisit(db, visitName);

  if (!visit)
    return withDelay(null);

  const planItems = state.planItems[visit.id] || [];
  const diagnoses = state.diagnoses[visit.id] || defaultDiagnosisForVisit(visit);
  const medications = state.medications[visit.id] || [];
  const clinicalNote = state.clinicalNotes[visit.id] || {};
  const ownerInstructions = state.ownerInstructions[visit.id] || visit.instructions || "";
  const caseContext = buildMockCaseContext(visit, state);
  const workbench = {
    visit_name: visit.id,
    visit,
    clinical: {
      intake_summary: visit.intakeSummary,
      overview: visit.overview,
      examination_notes: visit.examination,
      diagnosis: visit.assessment,
      treatment_plan: visit.plan,
      assessment_findings: [],
      assessment_note: "",
      client_observations: [],
      doctor_note: "",
      owner_instruction_items: [],
      owner_instruction_note: ownerInstructions,
      ...clinicalNote,
    },
    assessment_finding_options: MOCK_ASSESSMENT_FINDING_OPTIONS,
    client_observation_options: MOCK_CLIENT_OBSERVATION_OPTIONS,
    owner_instruction_options: MOCK_OWNER_INSTRUCTION_OPTIONS,
    pet: visit.pet,
    guardian: visit.guardian,
    case_context: caseContext,
    active_episode: caseContext.active_episode,
    case_sheet: buildMockCaseSheet(visit),
    vitals: latestVitalsForVisit(state, visit.id),
    vital_history: state.vitalHistory[visit.id] || [],
    diagnoses,
    medications,
    orders: visit.orders || [],
    linked_records: mockLinkedRecordsFromOrders(visit.orders || []),
    plan_items: planItems,
    visit_history: buildVisitHistory(db, visit.pet.id, visit.id),
    owner_instructions: ownerInstructions,
    billing: visit.billing,
    consultation_timer: state.consultationTimers[visit.id]
      ?? consultationTimerBlock({}, !/^(completed|cancelled)$/i.test(String(visit.status ?? ""))),
  };

  return withDelay({
    ...workbench,
    completion_checklist: completionChecklist(workbench),
    raw: {
      mock: true,
      visit: visit.id,
    },
  });
}

export async function mockCreateVisitOrders(visitName: string, orders: VisitOrderItem[]): Promise<any> {
  const db = loadDb();
  const visit = getWorkbenchVisit(db, visitName);

  if (!visit)
    return withDelay(null);

  const createdOrders = orders.map((order, index) => {
    const linkedDoctype = order.linkedDoctype || mockLinkedDoctype(order.kind);
    const linkedName = order.linkedName || mockLinkedName(order.kind, index);

    return {
      ...clone(order),
      id: order.id || `ORD-MOCK-${Date.now()}-${index + 1}`,
      orderId: order.orderId || order.id || `ORD-MOCK-${index + 1}`,
      status: order.status || "Ordered",
      linkedDoctype,
      linkedName,
    } as VisitOrderItem;
  });

  visit.orders = [...(visit.orders || []), ...createdOrders];
  visit.billing = {
    ...visit.billing,
    total: visit.orders.reduce((sum, order) => {
      const qty = Number(order.quantity ?? order.qty ?? 1);
      const rate = Number(order.rate ?? order.price ?? 0);

      return sum + qty * rate;
    }, 0),
  };

  saveDb(db);

  return withDelay({
    orders: visit.orders,
    linked_records: mockLinkedRecordsFromOrders(visit.orders),
    billing: visit.billing,
  });
}

export async function mockSetVisitCaseChoice(
  visitName: string,
  payload: { doctor_case_choice?: "wellness" | "continue_case" | "new_case" | null },
): Promise<any> {
  const db = loadDb();
  const state = loadWorkbenchState();
  const visit = getWorkbenchVisit(db, visitName);

  if (!visit)
    return withDelay(null);

  const choice = payload.doctor_case_choice || "wellness";

  state.caseChoices[visit.id] = choice;
  saveWorkbenchState(state);

  return withDelay({
    case_context: buildMockCaseContext(visit, state),
  });
}

export async function mockLinkCaseSheetToVisit(
  visitName: string,
  caseSheetId: string,
): Promise<{ visitId: string; caseSheetId: string }> {
  const db = loadDb();
  const visit = getWorkbenchVisit(db, visitName);
  const caseSheet = findCaseSheet(db, caseSheetId);

  if (!visit || !caseSheet)
    throw new Error("Visit or case sheet was not found.");

  visit.caseSheetId = caseSheet.id;
  visit.caseSheetNo = caseSheet.caseSheetNo;
  visit.linkedCaseSheet = caseSheet;
  visit.chiefComplaint = caseSheet.chiefComplaint || visit.chiefComplaint;
  visit.intakeSummary = caseSheet.intakeSummary || visit.intakeSummary;
  caseSheet.linkedVisitId = visit.id;
  caseSheet.linkedVisitStatus = visit.status || null;
  caseSheet.status = "In Consultation";

  saveDb(db);

  return withDelay({
    visitId: visit.id,
    caseSheetId: caseSheet.id,
  });
}

export async function mockAddPlanItemFromVisit(payload: Record<string, unknown>): Promise<any> {
  const state = loadWorkbenchState();
  const visitId = String(payload.visit || payload.visit_name || "VIS-001");
  const rows = state.planItems[visitId] || [];
  const created = {
    ...payload,
    name: `PLAN-MOCK-${Date.now()}`,
    title: payload.title || payload.reason || payload.medication || payload.item_type || "Mock plan item",
    status: payload.requires_appointment ? "Needs Appointment" : "Planned",
    priority: payload.priority || "Normal",
    due_date: payload.due_date || payload.due_datetime || null,
    requires_appointment: payload.requires_appointment ? 1 : 0,
    requires_reminder: payload.requires_reminder ? 1 : 0,
    owner_instructions: payload.owner_instructions || payload.instructions || null,
    visit: visitId,
  };

  state.planItems[visitId] = [created, ...rows];
  saveWorkbenchState(state);

  return withDelay(created);
}

function updateMockPlanItem(planItemName: string, patch: Record<string, unknown>) {
  const state = loadWorkbenchState();
  let updated: Record<string, unknown> | null = null;

  for (const [visitId, rows] of Object.entries(state.planItems)) {
    state.planItems[visitId] = rows.map((item) => {
      if (item.name !== planItemName)
        return item;

      updated = { ...item, ...patch };
      return updated;
    });
  }

  saveWorkbenchState(state);

  return updated;
}

export async function mockCompletePlanItem(planItemName: string): Promise<any> {
  return withDelay(updateMockPlanItem(planItemName, { status: "Done" }) || {});
}

export async function mockCancelPlanItem(planItemName: string, reason?: string): Promise<any> {
  return withDelay(updateMockPlanItem(planItemName, { status: "Cancelled", cancel_reason: reason || null }) || {});
}

export async function mockSchedulePlanItemAppointment(
  planItemName: string,
  payload: Record<string, unknown>,
): Promise<any> {
  return withDelay(updateMockPlanItem(planItemName, {
    status: "Scheduled",
    appointment: `APT-MOCK-${Date.now()}`,
    appointment_datetime: payload.appointment_datetime || payload.scheduled_time || null,
  }) || {});
}

export async function mockSaveVisitWorkbenchSection(
  visitName: string,
  action: string,
  payload: Record<string, unknown>,
): Promise<any> {
  const state = loadWorkbenchState();
  const visitId = String(visitName || payload.visit || "VIS-001");
  const now = getNowIso().slice(0, 19).replace("T", " ");

  if (action === "save_vitals") {
    const row = {
      ...payload,
      recorded_at: now,
      visit: visitId,
      episode: `PCE-${visitId}-ACTIVE`,
    };

    state.vitals[visitId] = row;
    state.vitalHistory[visitId] = [...(state.vitalHistory[visitId] || []), row];
  } else if (action === "save_diagnosis") {
    state.diagnoses[visitId] = [
      {
        ...payload,
        name: `DX-MOCK-${Date.now()}`,
      },
      ...(state.diagnoses[visitId] || []),
    ];
  } else if (action === "save_diagnoses") {
    const rows = Array.isArray(payload.diagnoses) ? payload.diagnoses : [];

    state.diagnoses[visitId] = rows.map((row: any, index) => ({
      ...row,
      name: row?.name || `DX-MOCK-${Date.now()}-${index}`,
    }));
  } else if (action === "save_prescribed_medication") {
    const rows = state.medications[visitId] || [];
    const rowName = String(payload.name || "");

    state.medications[visitId] = rowName && rows.some((row: any) => String(row?.name || "") === rowName)
      ? rows.map((row: any) =>
          String(row?.name || "") === rowName
            ? {
                ...row,
                ...payload,
                name: rowName,
                dispense_status: payload.dispense_status || row.dispense_status || "Prescribed",
              }
            : row,
        )
      : [
          ...rows,
          {
            ...payload,
            name: `MED-MOCK-${Date.now()}`,
            dispense_status: payload.dispense_status || "Prescribed",
          },
        ];
  } else if (action === "cancel_prescribed_medication") {
    const rows = state.medications[visitId] || [];
    const rowName = String(payload.row_name || "");
    const reason = String(payload.reason || "").trim();

    state.medications[visitId] = rows.map((row: any) => {
      if (String(row?.name || "") !== rowName)
        return row;

      const existingInstructions = String(row.instructions || "").trim();
      const cancelLine = reason ? `Not given: ${reason}` : "Not given";

      return {
        ...row,
        instructions: existingInstructions
          ? `${existingInstructions}\n${cancelLine}`
          : cancelLine,
        dispense_status: "Cancelled",
      };
    });
  } else if (action === "save_clinical_note") {
    state.clinicalNotes[visitId] = {
      ...(state.clinicalNotes[visitId] || {}),
      ...payload,
    };
    if (payload.instructions)
      state.ownerInstructions[visitId] = String(payload.instructions);
  } else if (action === "save_owner_instructions") {
    state.ownerInstructions[visitId] = String(payload.owner_instructions || payload.instructions || "");
  }

  saveWorkbenchState(state);

  return withDelay({ ok: true });
}

export async function mockCompleteVisitFromWorkbench(visitName: string): Promise<any> {
  const db = loadDb();
  const visit = getWorkbenchVisit(db, visitName);
  if (visit) {
    visit.status = "Completed";

    // Like the backend: completing stops a clock that is still running, then locks it.
    const state = loadWorkbenchState();
    const finished = applyConsultationTimerAction(state.consultationTimers[visit.id], "finish");
    state.consultationTimers[visit.id] = finished.started_at
      ? consultationTimerBlock(finished as Parameters<typeof consultationTimerBlock>[0], false)
      : consultationTimerBlock({}, false);
    saveWorkbenchState(state);
  }
  saveDb(db);

  return withDelay({ ok: true });
}

export async function mockConsultationTimerAction(visitName: string, action: ConsultationTimerAction): Promise<any> {
  const db = loadDb();
  const state = loadWorkbenchState();
  const visit = getWorkbenchVisit(db, visitName);

  if (!visit)
    return withDelay(null);

  state.consultationTimers[visit.id] = applyConsultationTimerAction(state.consultationTimers[visit.id], action);
  saveWorkbenchState(state);

  return withDelay({ ok: true });
}

export async function mockListDuePlanItems(): Promise<FrappeListResult<any>> {
  const state = loadWorkbenchState();
  const rows = Object.values(state.planItems)
    .flat()
    .filter(item => Boolean(item.requires_appointment) && !item.appointment);

  return withDelay({
    data: rows,
    total: rows.length,
    hasMore: false,
  });
}

// Cross-visit follow-up items for the tracking board. Returns the mock store's
// follow-up plan items, or a representative spread (overdue/today/upcoming/
// attended/missed) when the store has none so the board is demoable standalone.
export async function mockListFollowUpPlanItems(): Promise<FrappeListResult<any>> {
  const state = loadWorkbenchState();
  const stored = Object.values(state.planItems)
    .flat()
    .filter(item => String(item.item_type ?? "").toLowerCase().includes("follow"));

  const dayOffset = (days: number) => {
    const d = new Date();
    d.setDate(d.getDate() + days);

    return d.toISOString().slice(0, 10);
  };

  const rows = stored.length
    ? stored
    : [
        { name: "FU-DEMO-OVERDUE", item_type: "Follow-up Visit", title: "Recheck vomiting response", status: "Open", due_date: dayOffset(-2), requires_appointment: 0, pet: "PET-002", pet_name: "Rocky", guardian: "GUARD-002", guardian_name: "Omar Hassan", doctor_name: "Dr. Sara Ahmed", visit: "VST-DEMO-0001" },
        { name: "FU-DEMO-TODAY", item_type: "Follow-up Visit", title: "Daily recheck — day 2", status: "Open", due_date: dayOffset(0), requires_appointment: 0, pet: "PET-002", pet_name: "Rocky", guardian: "GUARD-002", guardian_name: "Omar Hassan", doctor_name: "Dr. Sara Ahmed", visit: "VST-DEMO-0001" },
        { name: "FU-DEMO-UPCOMING", item_type: "Follow-up Visit", title: "Suture removal", status: "Scheduled", due_date: dayOffset(4), requires_appointment: 1, appointment: "APPT-DEMO-1", pet: "PET-001", pet_name: "Luna", guardian: "GUARD-001", guardian_name: "Sarah Ahmed", doctor_name: "Dr. Rana Yousif", visit: "VST-DEMO-0002" },
        { name: "FU-DEMO-ATTENDED", item_type: "Follow-up Visit", title: "Daily recheck — day 1", status: "Done", due_date: dayOffset(-1), requires_appointment: 0, pet: "PET-002", pet_name: "Rocky", guardian: "GUARD-002", guardian_name: "Omar Hassan", doctor_name: "Dr. Sara Ahmed", visit: "VST-DEMO-0001" },
        { name: "FU-DEMO-MISSED", item_type: "Follow-up Visit", title: "Wound check", status: "Missed", due_date: dayOffset(-1), requires_appointment: 0, pet: "PET-004", pet_name: "Bella", guardian: "GUARD-004", guardian_name: "Noor Ali", doctor_name: "Dr. Rana Yousif", visit: "VST-DEMO-0003" },
      ];

  return withDelay({
    data: rows,
    total: rows.length,
    hasMore: false,
  });
}

function findCaseSheet(db: HealthcareMockDb, caseSheetId: string) {
  return db.caseSheets.find(item => item.id === caseSheetId) || null;
}

function findHealthcarePet(db: HealthcareMockDb, petId: string) {
  return [
    ...db.appointments.map(item => item.pet),
    ...db.caseSheets.map(item => item.pet),
    ...db.visits.map(item => item.pet),
    ...db.boarding.map(item => item.pet).filter(Boolean) as HealthcarePetSummary[],
  ].find(item => item.id === petId) || null;
}

function findHealthcareGuardian(db: HealthcareMockDb, guardianId: string) {
  return [
    ...db.appointments.map(item => item.guardian),
    ...db.caseSheets.map(item => item.guardian),
    ...db.visits.map(item => item.guardian),
    ...db.boarding.map(item => item.guardian).filter(Boolean) as HealthcareGuardianSummary[],
  ].find(item => item.id === guardianId) || null;
}

function findVisit(db: HealthcareMockDb, visitId: string) {
  return db.visits.find(item => item.id === visitId) || null;
}

function findAppointment(db: HealthcareMockDb, appointmentId: string) {
  return db.appointments.find(item => item.id === appointmentId) || null;
}

function findStandaloneService(db: HealthcareMockDb, serviceId: string) {
  return db.services.find(item => item.id === serviceId) || null;
}

function findAnyServiceRecord(db: HealthcareMockDb, serviceId: string) {
  const standalone = findStandaloneService(db, serviceId);
  if (standalone)
    return standalone;

  for (const visit of db.visits) {
    const record = (visit.serviceRecords || []).find(item => item.id === serviceId);
    if (record)
      return record;
  }

  return null;
}

function getAllServiceRecords(db: HealthcareMockDb) {
  const visitLinked = db.visits.flatMap(item => item.serviceRecords || []);
  return [...db.services, ...visitLinked];
}

function getAllLabRecords(db: HealthcareMockDb) {
  return db.visits.flatMap(item => item.labRecords || []);
}

function getAllRadiologyRecords(db: HealthcareMockDb) {
  return db.visits.flatMap(item => item.radiologyRecords || []);
}

function nextId(prefix: string, existing: string[]) {
  const max = existing.reduce((acc, current) => {
    const value = Number(current.replace(/\D+/g, ""));
    return Number.isFinite(value) ? Math.max(acc, value) : acc;
  }, 0);

  return `${prefix}${String(max + 1).padStart(3, "0")}`;
}

export async function mockGetDoctorByLinkedUser(username: string): Promise<Doctor | null> {
  const normalized = username.trim();
  if (!normalized)
    return withDelay(null);

  const seededDoctors: Doctor[] = [
    {
      id: "DOC-001",
      doctorName: "Dr. Rana Yousif",
      phone: "07700000111",
      specialization: "General Practice",
      user: "doctor@example.com",
      disabled: false,
      modified: "2026-04-20 09:00:00",
    },
    {
      id: "DOC-002",
      doctorName: "Dr. Admin",
      phone: "07700000112",
      specialization: "Clinical Operations",
      user: "admin@example.com",
      disabled: false,
      modified: "2026-04-20 09:05:00",
    },
    {
      id: "DOC-003",
      doctorName: "Dr. Administrator",
      phone: "07700000113",
      specialization: "General Practice",
      user: "Administrator",
      disabled: false,
      modified: "2026-04-20 09:10:00",
    },
  ];

  return withDelay(
    seededDoctors.find(doctor =>
      !doctor.disabled && doctor.user === normalized,
    ) || null,
  );
}

export async function mockGetAppointments(
  filter: AppointmentFilterParam = {},
): Promise<FrappeListResult<AppointmentDetail>> {
  const db = loadDb();
  const filtered = db.appointments.filter((item) => {
    const matchesSearch = includesQuery([
      item.customerName,
      item.customerEmail,
      item.customerPhoneNumber,
      item.pet.name,
      item.guardian.name,
      item.customerDetails,
      item.selectedServiceTemplateName,
    ], filter.search);

    const matchesStatus = sameOrEmpty(item.status, filter.status);
    const matchesType = sameOrEmpty(item.customAppointmentType, filter.appointmentType);
    const matchesPet = !filter.customPet || item.customPet === filter.customPet || item.pet.id === filter.customPet;
    const matchesGuardian = !filter.customGuardian || item.customGuardian === filter.customGuardian || item.guardian.id === filter.customGuardian;
    const matchesDates = matchesDateRange(item.scheduledTime, filter.scheduledFrom, filter.scheduledTo);

    return matchesSearch && matchesStatus && matchesType && matchesPet && matchesGuardian && matchesDates;
  });

  return withDelay(
    paginate(
      sortByOrder(
        filtered.map(item => ({
          ...item,
          modified: item.scheduledTime || item.createdAt,
        })),
        filter.order_by || "scheduledTime desc",
      ) as AppointmentDetail[],
      filter.limit_start ?? 0,
      filter.limit_page_length ?? 10,
    ),
  );
}

export async function mockGetAppointmentById(appointmentId: string): Promise<AppointmentDetail | null> {
  const db = loadDb();
  return withDelay(findAppointment(db, appointmentId));
}

export async function mockSaveAppointment(
  payload: AppointmentPayload,
): Promise<{ appointmentId: string } | null> {
  const db = loadDb();
  const existing = payload.id ? findAppointment(db, payload.id) : null;
  const scheduledTime = payload.scheduledTime?.trim() || getNowIso().slice(0, 19).replace("T", " ");
  const scheduledDate = scheduledTime.slice(0, 10);
  const scheduledClock = scheduledTime.slice(11, 16);
  const pet = [
    ...db.appointments.map(item => item.pet),
    ...db.caseSheets.map(item => item.pet),
    ...db.visits.map(item => item.pet),
    ...db.boarding.map(item => item.pet).filter(Boolean) as HealthcarePetSummary[],
  ].find(item => item.id === payload.customPet) || null;
  const guardian = [
    ...db.appointments.map(item => item.guardian),
    ...db.caseSheets.map(item => item.guardian),
    ...db.visits.map(item => item.guardian),
    ...db.boarding.map(item => item.guardian).filter(Boolean) as HealthcareGuardianSummary[],
  ].find(item => item.phone === payload.customerPhoneNumber) || null;

  const linkedCustomer = payload.customCustomer
    || existing?.customCustomer
    || (guardian ? `CUST-${guardian.id}` : null);

  if (!pet)
    return withDelay(null);

  if (existing) {
    existing.petId = pet.id;
    existing.guardianId = guardian?.id ?? existing.guardianId ?? null;
    existing.pet = pet;
    if (guardian)
      existing.guardian = guardian;
    existing.customerName = payload.customerName;
    existing.customerEmail = payload.customerEmail;
    existing.customerPhoneNumber = payload.customerPhoneNumber ?? null;
    existing.customerSkype = payload.customerSkype ?? null;
    existing.customerDetails = payload.customerDetails ?? null;
    existing.scheduledTime = scheduledTime;
    existing.scheduledDate = scheduledDate;
    existing.scheduledClock = scheduledClock;
    existing.status = payload.status ?? existing.status;
    existing.customAppointmentType = payload.customAppointmentType ?? existing.customAppointmentType ?? null;
    existing.customPet = payload.customPet ?? existing.customPet ?? null;
    existing.customGuardian = guardian?.id ?? existing.customGuardian ?? null;
    existing.customCustomer = linkedCustomer;
    existing.appointmentWith = guardian ? "Customer" : existing.appointmentWith ?? null;
    existing.party = linkedCustomer;
    existing.selectedServiceTemplateId = payload.selectedServiceTemplateId ?? null;
    existing.selectedServiceTemplateName = payload.selectedServiceTemplateName ?? null;
    existing.doctorId = payload.doctorId ?? existing.doctorId ?? null;
    existing.doctorName = payload.doctorName ?? existing.doctorName ?? null;
    existing.linkedVisitId = payload.linkedVisitId ?? existing.linkedVisitId ?? null;
    existing.linkedServiceId = payload.linkedServiceId ?? existing.linkedServiceId ?? null;
    existing.convertedTarget = payload.convertedTarget ?? existing.convertedTarget ?? null;
    existing.convertedAt = payload.convertedAt ?? existing.convertedAt ?? null;
    saveDb(db);

    return withDelay({ appointmentId: existing.id });
  }

  const appointmentId = nextId("APT-", db.appointments.map(item => item.id));
  db.appointments.unshift({
    id: appointmentId,
    customerName: payload.customerName,
    customerEmail: payload.customerEmail,
    customerPhoneNumber: payload.customerPhoneNumber ?? null,
    customerSkype: payload.customerSkype ?? null,
    customerDetails: payload.customerDetails ?? null,
    petId: pet.id,
    guardianId: guardian?.id ?? null,
    status: payload.status ?? "Open",
    pet,
    guardian: guardian ?? {
      id: "",
      name: "",
      phone: null,
      email: null,
      image: null,
    },
    scheduledTime,
    scheduledDate,
    scheduledClock,
    customAppointmentType: payload.customAppointmentType ?? null,
    customPet: payload.customPet ?? pet.id,
    customGuardian: guardian?.id ?? null,
    customCustomer: linkedCustomer,
    appointmentWith: guardian ? "Customer" : null,
    party: linkedCustomer,
    selectedServiceTemplateId: payload.selectedServiceTemplateId ?? null,
    selectedServiceTemplateName: payload.selectedServiceTemplateName ?? null,
    doctorId: payload.doctorId ?? null,
    doctorName: payload.doctorName ?? null,
    linkedVisitId: payload.linkedVisitId ?? null,
    linkedServiceId: payload.linkedServiceId ?? null,
    convertedTarget: payload.convertedTarget ?? null,
    convertedAt: payload.convertedAt ?? null,
    createdAt: getNowIso().slice(0, 19).replace("T", " "),
  });
  saveDb(db);

  return withDelay({ appointmentId });
}

export async function mockGetCaseSheets(
  filter: CaseSheetFilterParam,
): Promise<FrappeListResult<CaseSheetDetail>> {
  const db = loadDb();
  const filtered = db.caseSheets.filter((item) => {
    const matchesSearch = includesQuery([
      item.caseSheetNo,
      item.pet.name,
      item.guardian.name,
      item.chiefComplaint,
      item.intakeSummary,
    ], filter.search);

    const matchesStatus = sameOrEmpty(item.status, filter.status);
    const matchesPriority = sameOrEmpty(item.priority, filter.priority);
    const matchesPet = !filter.pet || item.pet.id === filter.pet || item.pet.name === filter.pet;
    const matchesGuardian = !filter.guardian || item.guardian.id === filter.guardian || item.guardian.name === filter.guardian;
    const matchesDates = matchesDateRange(item.createdAt, filter.dateFrom, filter.dateTo);

    return matchesSearch && matchesStatus && matchesPriority && matchesPet && matchesGuardian && matchesDates;
  });

  return withDelay(
    paginate(
      sortByOrder(filtered, filter.order_by),
      filter.limit_start ?? 0,
      filter.limit_page_length ?? 10,
    ),
  );
}

export async function mockGetCaseSheetById(caseSheetId: string): Promise<CaseSheetDetail | null> {
  const db = loadDb();
  return withDelay(findCaseSheet(db, caseSheetId));
}

export async function mockSaveCaseSheet(payload: CaseSheetPayload): Promise<{ caseSheetId: string } | null> {
  const db = loadDb();
  const existing = payload.id ? findCaseSheet(db, payload.id) : null;
  const pet = findHealthcarePet(db, payload.petId || "") || {
    id: payload.petId || "",
    name: payload.petId || "",
    image: null,
    species: null,
    breed: null,
    gender: null,
    age: null,
    weight: null,
  };
  const guardian = findHealthcareGuardian(db, payload.guardianId || "") || {
    id: payload.guardianId || "",
    name: payload.guardianId || "",
    phone: null,
    image: null,
    email: null,
  };
  const createdAt = existing?.createdAt || getNowIso().slice(0, 19).replace("T", " ");
  const caseSheetId = existing?.id || nextId("CASE-", db.caseSheets.map(item => item.id));
  const caseSheetNo = existing?.caseSheetNo || `VCS-${String(db.caseSheets.length + 1).padStart(5, "0")}`;
  const record: CaseSheetDetail = {
    ...(existing || {}),
    id: caseSheetId,
    caseSheetNo,
    pet,
    guardian,
    chiefComplaint: payload.chiefComplaint || null,
    intakeSummary: payload.intakeNotes || payload.complaintOther || payload.intakeSummary || null,
    priority: payload.priority || "Normal",
    status: payload.status || "Draft",
    billingStatus: payload.billingStatus || "Unbilled",
    createdAt,
    linkedVisitId: existing?.linkedVisitId || null,
    linkedVisitStatus: existing?.linkedVisitStatus || null,
    symptoms: [
      payload.hasVomiting ? "Vomiting" : "",
      payload.hasDiarrhea ? "Diarrhea" : "",
      payload.hasCough ? "Cough" : "",
      payload.hasSneezing ? "Sneezing" : "",
      payload.hasLossOfAppetite ? "Loss of Appetite" : "",
      payload.hasLethargy ? "Lethargy" : "",
      payload.hasItching ? "Itching" : "",
      payload.hasWound ? "Wound" : "",
      payload.hasLimping ? "Limping" : "",
      payload.hasBreathingIssue ? "Breathing Issue" : "",
      payload.hasEyeDischarge ? "Eye Discharge" : "",
      payload.hasEarDischarge ? "Ear Discharge" : "",
      payload.hasFeverFlag ? "Fever Flag" : "",
    ].filter(Boolean).join(", ") || null,
    background: [
      payload.vaccinationStatus,
      payload.neuteredSpayed,
      payload.chronicDisease ? payload.chronicDiseaseDetails || "Chronic Disease" : "",
      payload.currentlyOnMedication ? payload.medicationDetails || "Currently on Medication" : "",
    ].filter(Boolean).join(" / ") || null,
    history: [
      payload.symptomDuration,
      payload.previousVetVisit ? payload.previousVetDetails || "Previous Vet Visit" : "",
      payload.appetiteStatus,
      payload.waterIntakeStatus,
      payload.urinationStatus,
      payload.defecationStatus,
      payload.activityStatus,
    ].filter(Boolean).join(" / ") || null,
    triageNotes: payload.intakeNotes || null,
    requestedBy: existing?.requestedBy || guardian.name,
    practitionerName: existing?.practitionerName || null,
    sections: [],
  };

  record.sections = buildCaseSheetSections(record);

  if (existing) {
    db.caseSheets = db.caseSheets.map(item => item.id === caseSheetId ? record : item);
  } else {
    db.caseSheets.unshift(record);
  }

  saveDb(db);

  return withDelay({ caseSheetId });
}

export async function mockStartVisitFromCaseSheet(caseSheetId: string): Promise<StartVisitResult | null> {
  const db = loadDb();
  const caseSheet = findCaseSheet(db, caseSheetId);

  if (!caseSheet)
    return withDelay(null);

  if (caseSheet.linkedVisitId) {
    return withDelay({
      visitId: caseSheet.linkedVisitId,
      caseSheetId,
      created: false,
    });
  }

  const visitId = nextId("VIS-", db.visits.map(item => item.id));
  const startedAt = getNowIso().slice(0, 19).replace("T", " ");
  const visitDate = startedAt.slice(0, 10);
  const visitTime = startedAt.slice(11, 16);

  const createdVisit: VisitDetail = {
    id: visitId,
    caseSheetId: caseSheet.id,
    caseSheetNo: caseSheet.caseSheetNo,
    status: "Waiting Doctor",
    priority: caseSheet.priority || "Normal",
    practitionerId: null,
    practitionerName: null,
    practitionerImage: null,
    visitDate,
    visitTime,
    startedAt,
    pet: caseSheet.pet,
    guardian: caseSheet.guardian,
    chiefComplaint: caseSheet.chiefComplaint,
    intakeSummary: caseSheet.intakeSummary,
    overview: caseSheet.intakeSummary ? `<p>${caseSheet.intakeSummary}</p>` : "",
    examination: "",
    assessment: "",
    plan: "",
    instructions: "",
    historyItems: buildVisitHistory(db, caseSheet.pet.id),
    orders: [],
    labRecords: [],
    radiologyRecords: [],
    serviceRecords: [],
    billing: {
      status: caseSheet.billingStatus || "Unbilled",
      currency: "IQD",
      total: 0,
      deposit: 0,
      paid: 0,
      balance: 0,
    },
    linkedCaseSheet: clone(caseSheet),
  };

  db.visits.unshift(createdVisit);
  caseSheet.linkedVisitId = visitId;
  caseSheet.linkedVisitStatus = createdVisit.status;
  caseSheet.status = "In Consultation";
  saveDb(db);

  return withDelay({
    visitId,
    caseSheetId,
    created: true,
  });
}

export async function mockGetVisits(
  filter: VisitFilterParam,
): Promise<FrappeListResult<VisitDetail>> {
  const db = loadDb();
  const { consultationTimers } = loadWorkbenchState();
  const filtered = db.visits.filter((item) => {
    const matchesSearch = includesQuery([
      item.pet.name,
      item.guardian.name,
      item.caseSheetNo,
      item.chiefComplaint,
      item.practitionerName,
    ], filter.search);

    const matchesStatus = sameOrEmpty(item.status, filter.status);
    const matchesPriority = sameOrEmpty(item.priority, filter.priority);
    const matchesBilling = sameOrEmpty(item.billing.status, filter.billingStatus);
    const matchesCaseSheet = !filter.caseSheetId || item.caseSheetId === filter.caseSheetId;
    const matchesPet = !filter.petId || item.pet.id === filter.petId;
    const matchesGuardian = !filter.guardianId || item.guardian.id === filter.guardianId;

    return matchesSearch && matchesStatus && matchesPriority && matchesBilling && matchesCaseSheet && matchesPet && matchesGuardian;
  });

  return withDelay(
    paginate(
      sortByOrder(
        filtered.map(item => ({
          ...item,
          modified: item.startedAt,
          consultation_seconds: consultationTimers[item.id]?.active_seconds ?? null,
        })),
        filter.order_by,
      ) as VisitDetail[],
      filter.limit_start ?? 0,
      filter.limit_page_length ?? 10,
    ),
  );
}

export async function mockGetVisitById(visitId: string): Promise<VisitDetail | null> {
  const db = loadDb();
  const visit = findVisit(db, visitId);

  if (!visit)
    return withDelay(null);

  return withDelay({
    ...visit,
    historyItems: buildVisitHistory(db, visit.pet.id, visit.id),
  });
}

export async function mockGetVisitHistory(
  petId?: string | null,
  visitId?: string | null,
): Promise<VisitHistoryItem[]> {
  const db = loadDb();
  return withDelay(buildVisitHistory(db, petId, visitId));
}

export async function mockSaveVisit(
  payload: VisitUpsertPayload,
): Promise<{ visitId: string } | null> {
  const db = loadDb();
  const startedAt = getNowIso().slice(0, 19).replace("T", " ");
  const existing = payload.id ? findVisit(db, payload.id) : null;

  if (existing) {
    existing.caseSheetId = payload.caseSheetId ?? existing.caseSheetId;
    existing.status = payload.status ?? existing.status;
    existing.priority = payload.priority ?? existing.priority;
    existing.practitionerId = payload.practitionerId ?? existing.practitionerId;
    existing.practitionerName = payload.practitionerName ?? existing.practitionerName;
    existing.chiefComplaint = payload.chiefComplaint ?? existing.chiefComplaint;
    existing.intakeSummary = payload.intakeSummary ?? existing.intakeSummary;
    existing.overview = payload.overview ?? existing.overview;
    existing.examination = payload.examination ?? existing.examination;
    existing.assessment = payload.assessment ?? existing.assessment;
    existing.plan = payload.plan ?? existing.plan;
    existing.instructions = payload.instructions ?? existing.instructions;
    existing.orders = clone(payload.orders || []);
    existing.labRecords = clone(payload.labRecords || []);
    existing.radiologyRecords = clone(payload.radiologyRecords || []);
    existing.serviceRecords = clone(payload.serviceRecords || []);
    existing.billing = clone(payload.billing);
    saveDb(db);

    if (existing.caseSheetId) {
      const linkedCaseSheet = findCaseSheet(db, existing.caseSheetId);
      if (linkedCaseSheet) {
        linkedCaseSheet.linkedVisitId = existing.id;
        linkedCaseSheet.linkedVisitStatus = existing.status || null;
        linkedCaseSheet.status = existing.status || linkedCaseSheet.status;
      }
    }

    saveDb(db);
    return withDelay({ visitId: existing.id });
  }

  const visitId = nextId("VIS-", db.visits.map(item => item.id));
  const caseSheet = payload.caseSheetId ? findCaseSheet(db, payload.caseSheetId) : null;

  const createdVisit: VisitDetail = {
    id: visitId,
    caseSheetId: payload.caseSheetId ?? null,
    caseSheetNo: caseSheet?.caseSheetNo ?? null,
    status: payload.status ?? "Draft",
    priority: payload.priority ?? "Normal",
    practitionerId: payload.practitionerId ?? null,
    practitionerName: payload.practitionerName ?? null,
    practitionerImage: null,
    visitDate: startedAt.slice(0, 10),
    visitTime: startedAt.slice(11, 16),
    startedAt,
    pet: caseSheet?.pet || {
      id: payload.petId || "PET-NEW",
      name: "Selected Pet",
      image: null,
    },
    guardian: caseSheet?.guardian || {
      id: payload.guardianId || "GUARD-NEW",
      name: "Selected Guardian",
      image: null,
    },
    chiefComplaint: payload.chiefComplaint ?? null,
    intakeSummary: payload.intakeSummary ?? null,
    overview: payload.overview ?? "",
    examination: payload.examination ?? "",
    assessment: payload.assessment ?? "",
    plan: payload.plan ?? "",
    instructions: payload.instructions ?? "",
    historyItems: [],
    orders: clone(payload.orders || []),
    labRecords: clone(payload.labRecords || []),
    radiologyRecords: clone(payload.radiologyRecords || []),
    serviceRecords: clone(payload.serviceRecords || []),
    billing: clone(payload.billing),
    linkedCaseSheet: caseSheet ? clone(caseSheet) : null,
  };

  db.visits.unshift(createdVisit);

  if (caseSheet) {
    caseSheet.linkedVisitId = visitId;
    caseSheet.linkedVisitStatus = createdVisit.status || null;
    caseSheet.status = createdVisit.status || caseSheet.status;
  }

  saveDb(db);
  return withDelay({ visitId });
}

type MockClinicalRecordKind = "lab" | "radiology" | "service" | "procedure";

export async function mockGetClinicalRecords(
  kind: MockClinicalRecordKind,
  filter: {
    search?: string;
    status?: string | null;
    visitId?: string | null;
    petId?: string | null;
    limit_start?: number;
    limit_page_length?: number;
    order_by?: string;
  } = {},
): Promise<FrappeListResult<VisitLabRecord | VisitRadiologyRecord | VisitServiceRecord>> {
  const db = loadDb();
  const source = kind === "lab"
    ? getAllLabRecords(db)
    : kind === "radiology"
      ? getAllRadiologyRecords(db)
      : getAllServiceRecords(db);

  const filtered = source.filter((item) => {
    const title = kind === "lab"
      ? (item as VisitLabRecord).testName
      : kind === "radiology"
        ? (item as VisitRadiologyRecord).studyName
        : (item as VisitServiceRecord).serviceName;
    const subtitle = kind === "lab"
      ? (item as VisitLabRecord).specimen
      : kind === "radiology"
        ? (item as VisitRadiologyRecord).modality
        : (item as VisitServiceRecord).assignedTo;

    const matchesSearch = includesQuery([title, subtitle, item.id], filter.search);
    const matchesStatus = sameOrEmpty(item.status, filter.status);
    const matchesVisit = !filter.visitId || item.visitId === filter.visitId;

    if (!matchesSearch || !matchesStatus || !matchesVisit)
      return false;

    if (!filter.petId)
      return true;

    if (item.visitId) {
      const visit = findVisit(db, item.visitId);
      return visit?.pet.id === filter.petId;
    }

    const appointment = db.appointments.find(row => row.linkedServiceId === item.id);
    return appointment?.pet.id === filter.petId;
  });

  return withDelay(
    paginate(
      sortByOrder(
        filtered.map(item => ({
          ...item,
          modified: kind === "service" || kind === "procedure"
            ? (item as VisitServiceRecord).performedAt || item.id
            : kind === "lab"
              ? (item as VisitLabRecord).releasedAt || item.id
              : (item as VisitRadiologyRecord).resultEnteredAt || item.id,
        })),
        filter.order_by || "modified desc",
      ) as Array<VisitLabRecord | VisitRadiologyRecord | VisitServiceRecord>,
      filter.limit_start ?? 0,
      filter.limit_page_length ?? 20,
    ),
  );
}

export async function mockGetClinicalRecordById(
  kind: MockClinicalRecordKind,
  id: string,
): Promise<VisitLabRecord | VisitRadiologyRecord | VisitServiceRecord | null> {
  const db = loadDb();

  if (kind === "lab")
    return withDelay(getAllLabRecords(db).find(item => item.id === id) || null);

  if (kind === "radiology")
    return withDelay(getAllRadiologyRecords(db).find(item => item.id === id) || null);

  return withDelay(getAllServiceRecords(db).find(item => item.id === id) || null);
}

export async function mockUpdateClinicalRecord(
  kind: MockClinicalRecordKind,
  id: string,
  payload: Record<string, unknown>,
): Promise<boolean> {
  const db = loadDb();
  const visitCollections = db.visits.map(visit => ({
    labRecords: visit.labRecords || [],
    radiologyRecords: visit.radiologyRecords || [],
    serviceRecords: visit.serviceRecords || [],
  }));

  const standaloneService = kind === "service" || kind === "procedure" ? findStandaloneService(db, id) : null;
  if (standaloneService) {
    if (payload.status !== undefined)
      standaloneService.status = String(payload.status);
    if (payload.priority !== undefined)
      standaloneService.priority = String(payload.priority);
    if (payload.assigned_to !== undefined || payload.provider !== undefined)
      standaloneService.assignedTo = String(payload.assigned_to || payload.provider || "") || null;
    if (payload.note !== undefined || payload.description !== undefined)
      standaloneService.note = String(payload.note || payload.description || "") || null;
    if (payload.price !== undefined)
      standaloneService.price = Number(payload.price || 0) || null;
    if (payload.performed_at !== undefined)
      standaloneService.performedAt = String(payload.performed_at || "") || null;

    saveDb(db);
    return withDelay(true);
  }

  for (const collection of visitCollections) {
    const bucket = kind === "lab"
      ? collection.labRecords
      : kind === "radiology"
        ? collection.radiologyRecords
        : collection.serviceRecords;
    const record = bucket.find(item => item.id === id);

    if (!record)
      continue;

    if (kind === "lab") {
      if (payload.status !== undefined)
        (record as VisitLabRecord).status = String(payload.status);
      if (payload.priority !== undefined)
        (record as VisitLabRecord).priority = String(payload.priority);
      if (payload.specimen !== undefined)
        (record as VisitLabRecord).specimen = String(payload.specimen || "") || null;
      if (payload.result !== undefined)
        (record as VisitLabRecord).result = String(payload.result || "") || null;
      if (payload.result_file !== undefined)
        (record as VisitLabRecord).resultFile = String(payload.result_file || "") || null;
      if (payload.start_at !== undefined)
        (record as VisitLabRecord).startAt = String(payload.start_at || "") || null;
      if (payload.end_at !== undefined)
        (record as VisitLabRecord).endAt = String(payload.end_at || "") || null;
      if (payload.released_at !== undefined)
        (record as VisitLabRecord).releasedAt = String(payload.released_at || "") || null;
    } else if (kind === "radiology") {
      if (payload.status !== undefined)
        (record as VisitRadiologyRecord).status = String(payload.status);
      if (payload.priority !== undefined)
        (record as VisitRadiologyRecord).priority = String(payload.priority);
      if (payload.modality !== undefined)
        (record as VisitRadiologyRecord).modality = String(payload.modality || "") || null;
      if (payload.body_part !== undefined)
        (record as VisitRadiologyRecord).bodyPart = String(payload.body_part || "") || null;
      if (payload.report !== undefined)
        (record as VisitRadiologyRecord).report = String(payload.report || "") || null;
      if (payload.image !== undefined)
        (record as VisitRadiologyRecord).image = String(payload.image || "") || null;
      if (payload.report_file !== undefined)
        (record as VisitRadiologyRecord).reportFile = String(payload.report_file || "") || null;
      // Accepts the legacy `reported_at` key as well as the real one, so a mock caller written
      // against the old field keeps working while the record stores `resultEnteredAt`.
      if (payload.result_entered_at !== undefined || payload.reported_at !== undefined) {
        const stamp = payload.result_entered_at ?? payload.reported_at;

        (record as VisitRadiologyRecord).resultEnteredAt = String(stamp || "") || null;
      }
    } else {
      if (payload.status !== undefined)
        (record as VisitServiceRecord).status = String(payload.status);
      if (payload.priority !== undefined)
        (record as VisitServiceRecord).priority = String(payload.priority);
      if (payload.assigned_to !== undefined || payload.provider !== undefined)
        (record as VisitServiceRecord).assignedTo = String(payload.assigned_to || payload.provider || "") || null;
      if (payload.note !== undefined || payload.description !== undefined)
        (record as VisitServiceRecord).note = String(payload.note || payload.description || "") || null;
      if (payload.price !== undefined)
        (record as VisitServiceRecord).price = Number(payload.price || 0) || null;
      if (payload.performed_at !== undefined)
        (record as VisitServiceRecord).performedAt = String(payload.performed_at || "") || null;
    }

    saveDb(db);
    return withDelay(true);
  }

  return withDelay(false);
}

function normalizeMockServiceStatus(value: unknown) {
  const normalized = String(value ?? "").trim().toLowerCase();

  if (!normalized || normalized === "ordered" || normalized === "open")
    return "pending";
  if (["in progress", "in_progress", "in-progress", "processing"].includes(normalized))
    return "pending";
  if (["completed", "complete", "closed", "done"].includes(normalized))
    return "completed";
  if (normalized === "overdue")
    return "overdue";

  return "pending";
}

function currentMockDateTime() {
  return new Date().toISOString().slice(0, 19).replace("T", " ");
}

function mockServiceFieldValue(service: HealthcareServiceRecord, field: string) {
  const values: Record<string, unknown> = {
    name: service.name,
    pet_service_name: service.serviceName,
    pet_id: service.petId,
    care_service_id: service.careServiceId,
    "care_service_id.service_name": service.careServiceName,
    status: service.status,
    doctor: service.doctor,
    user: service.user,
    doc_type: service.sourceDocType,
    document: service.sourceDocument,
    visit: service.visitId,
    start_date: service.startDate,
    end_date: service.endDate,
    due_date: service.dueDate,
    provider: service.provider,
    description: service.description,
    specimen: service.specimen,
    modality: service.modality,
    body_part: service.bodyPart,
    result: service.result,
    result_file: service.resultFile,
    report: service.report,
    image: service.image,
    report_file: service.reportFile,
    send_reminder: service.sendReminder ? 1 : 0,
    amended_from: service.amendedFrom,
    rating: service.rating,
    docstatus: service.docstatus,
    creation: service.createdAt,
    modified: service.modifiedAt,
  };

  return values[field] ?? (service as unknown as Record<string, unknown>)[field];
}

function matchesMockClause(service: HealthcareServiceRecord, clause: FrappeFilterClause) {
  const field = clause.length === 4 ? clause[1] : clause[0];
  const operator = clause.length === 4 ? clause[2] : clause[1];
  const expected = clause.length === 4 ? clause[3] : clause[2];
  const actual = mockServiceFieldValue(service, String(field));

  if (operator === "like")
    return String(actual ?? "").toLowerCase().includes(String(expected ?? "").replace(/%/g, "").toLowerCase());
  if (operator === "not like")
    return !String(actual ?? "").toLowerCase().includes(String(expected ?? "").replace(/%/g, "").toLowerCase());
  if (operator === "=")
    return String(actual ?? "").toLowerCase() === String(expected ?? "").toLowerCase();
  if (operator === "!=")
    return String(actual ?? "").toLowerCase() !== String(expected ?? "").toLowerCase();
  if (operator === "in")
    return Array.isArray(expected) && expected.map(String).includes(String(actual ?? ""));
  if (operator === "not in")
    return Array.isArray(expected) && !expected.map(String).includes(String(actual ?? ""));
  if (operator === "is")
    return String(expected).toLowerCase() === "set" ? actual != null && actual !== "" : actual == null || actual === "";

  const left = String(actual ?? "");
  const right = String(expected ?? "");
  if (operator === ">=")
    return left >= right;
  if (operator === ">")
    return left > right;
  if (operator === "<=")
    return left <= right;
  if (operator === "<")
    return left < right;
  if (operator === "between" && Array.isArray(expected))
    return left >= String(expected[0] ?? "") && left <= String(expected[1] ?? "");

  return true;
}

function mapMockHealthcareService(db: HealthcareMockDb, source: VisitServiceRecord): HealthcareServiceRecord {
  const raw = source as Record<string, any>;
  const sourceDocType = raw.doc_type || (raw.visit || source.visitId ? "Visit" : null);
  const sourceDocument = raw.document || raw.visit || source.visitId || null;
  const visitId = (raw.visit ?? (String(sourceDocType).toLowerCase() === "visit" ? sourceDocument : source.visitId)) || null;
  const visit = visitId ? findVisit(db, visitId) : null;
  const appointment = db.appointments.find(row => row.linkedServiceId === source.id) || null;
  const pet = raw.pet
    || visit?.pet
    || appointment?.pet
    || {
      id: raw.petId || raw.pet_id || "",
      name: raw.petName || raw.pet_name || raw.petId || raw.pet_id || "",
      image: null,
      species: null,
      breed: null,
    };

  return {
    name: raw.name || source.id,
    id: raw.name || source.id,
    serviceName: raw.pet_service_name || source.serviceName,
    petId: raw.pet_id || raw.petId || pet.id || "",
    pet,
    careServiceId: raw.care_service_id || source.definitionId || "",
    careServiceName: raw["care_service_id.service_name"] || raw.careServiceName || raw.service_name || source.serviceName || null,
    status: normalizeMockServiceStatus(raw.status || source.status),
    doctor: raw.doctor || null,
    doctorName: raw.doctorName || raw.doctor_name || source.assignedTo || raw.doctor || null,
    user: raw.user || null,
    visitId,
    sourceDocType: sourceDocType || (appointment ? "Appointment" : null),
    sourceDocument: sourceDocument || appointment?.id || null,
    startDate: raw.start_date || raw.startDate || source.performedAt || null,
    endDate: raw.end_date || raw.endDate || null,
    dueDate: raw.due_date || raw.dueDate || null,
    provider: raw.provider || source.assignedTo || null,
    description: raw.description || source.note || null,
    specimen: raw.specimen || null,
    modality: raw.modality || null,
    bodyPart: raw.body_part || raw.bodyPart || null,
    result: raw.result || raw.results || raw.lab_result || null,
    resultFile: raw.result_file || raw.resultFile || null,
    report: raw.report || raw.radiology_report || null,
    image: raw.image || raw.radiology_image || raw.scan_image || null,
    reportFile: raw.report_file || raw.reportFile || null,
    sendReminder: Boolean(raw.send_reminder ?? raw.sendReminder ?? false),
    amendedFrom: raw.amended_from || raw.amendedFrom || null,
    rating: raw.rating == null ? null : Number(raw.rating),
    docstatus: Number(raw.docstatus ?? 0),
    createdAt: raw.creation || raw.createdAt || null,
    modifiedAt: raw.modified || raw.modifiedAt || raw.performedAt || null,
  };
}

function applyMockHealthcareServiceUpdate(
  record: VisitServiceRecord,
  payload: HealthcareServiceUpdatePayload,
) {
  const raw = record as Record<string, any>;

  if (payload.serviceName !== undefined) {
    record.serviceName = payload.serviceName || "";
    raw.pet_service_name = payload.serviceName || "";
  }
  if (payload.petId !== undefined)
    raw.pet_id = payload.petId || "";
  if (payload.careServiceId !== undefined) {
    record.definitionId = payload.careServiceId || null;
    raw.care_service_id = payload.careServiceId || null;
  }
  if (payload.status !== undefined)
    record.status = normalizeMockServiceStatus(payload.status);
  if (payload.doctor !== undefined)
    raw.doctor = payload.doctor || null;
  if (payload.visitId !== undefined) {
    record.visitId = payload.visitId || "";
    raw.visit = payload.visitId || null;
  }
  if (payload.sourceDocType !== undefined)
    raw.doc_type = payload.sourceDocType || null;
  if (payload.sourceDocument !== undefined)
    raw.document = payload.sourceDocument || null;
  if (payload.startDate !== undefined)
    raw.start_date = payload.startDate || null;
  if (payload.endDate !== undefined)
    raw.end_date = payload.endDate || null;
  if (payload.dueDate !== undefined)
    raw.due_date = payload.dueDate || null;
  if (payload.provider !== undefined) {
    raw.provider = payload.provider || null;
    record.assignedTo = payload.provider || null;
  }
  if (payload.description !== undefined) {
    raw.description = payload.description || null;
    record.note = payload.description || null;
  }
  if (payload.specimen !== undefined)
    raw.specimen = payload.specimen || null;
  if (payload.modality !== undefined)
    raw.modality = payload.modality || null;
  if (payload.bodyPart !== undefined)
    raw.body_part = payload.bodyPart || null;
  if (payload.result !== undefined)
    raw.result = payload.result || null;
  if (payload.resultFile !== undefined)
    raw.result_file = payload.resultFile || null;
  if (payload.report !== undefined)
    raw.report = payload.report || null;
  if (payload.image !== undefined)
    raw.image = payload.image || null;
  if (payload.reportFile !== undefined)
    raw.report_file = payload.reportFile || null;
  if (payload.sendReminder !== undefined)
    raw.send_reminder = payload.sendReminder ? 1 : 0;
  if (payload.rating !== undefined)
    raw.rating = payload.rating ?? null;

  raw.modified = currentMockDateTime();
}

export async function mockGetHealthcareServices(
  filter: HealthcareServiceFilterParam = {},
): Promise<FrappeListResult<HealthcareServiceRecord>> {
  const db = loadDb();
  const services = getAllServiceRecords(db).map(item => mapMockHealthcareService(db, item));
  const filtered = services.filter((item) => {
    const matchesSearch = includesQuery([
      item.id,
      item.serviceName,
      item.petId,
      item.careServiceId,
      item.doctorName,
      item.provider,
      item.visitId,
      item.sourceDocType,
      item.sourceDocument,
      item.description,
    ], filter.search);
    const matchesQuickFilters =
      sameOrEmpty(item.status, filter.status)
      && (!filter.doctor || includesQuery([item.doctor, item.doctorName], filter.doctor))
      && (!filter.provider || includesQuery([item.provider], filter.provider))
      && (!filter.visitId || item.visitId === filter.visitId)
      && (!filter.petId || item.petId === filter.petId)
      && (!filter.careServiceIds?.length || filter.careServiceIds.includes(item.careServiceId))
      && matchesDateRange(item.dueDate, filter.dueDateFrom, filter.dueDateTo)
      && matchesDateRange(item.startDate, filter.startDateFrom, filter.startDateTo)
      && matchesDateRange(item.endDate, filter.endDateFrom, filter.endDateTo)
      && (filter.rating == null || Number(item.rating ?? 0) === Number(filter.rating))
      && (filter.docstatus == null || Number(item.docstatus ?? 0) === Number(filter.docstatus));
    const matchesConditions = (filter.filters || []).every(clause => matchesMockClause(item, clause));
    const matchesOrConditions = !filter.orFilters?.length
      || filter.orFilters.some(clause => matchesMockClause(item, clause));

    return matchesSearch && matchesQuickFilters && matchesConditions && matchesOrConditions;
  });

  return withDelay(
    paginate(
      sortByOrder(filtered, filter.order_by || "modifiedAt desc"),
      filter.limit_start ?? 0,
      filter.limit_page_length ?? 20,
    ),
  );
}

export async function mockGetHealthcareServiceById(
  serviceId: string,
): Promise<HealthcareServiceRecord | null> {
  const db = loadDb();
  const record = findAnyServiceRecord(db, serviceId);

  return withDelay(record ? mapMockHealthcareService(db, record) : null);
}

export async function mockUpdateHealthcareService(
  serviceId: string,
  payload: HealthcareServiceUpdatePayload,
): Promise<HealthcareServiceRecord | null> {
  const db = loadDb();
  const record = findAnyServiceRecord(db, serviceId);
  if (!record)
    return withDelay(null);

  applyMockHealthcareServiceUpdate(record, payload);
  saveDb(db);

  return withDelay(mapMockHealthcareService(db, record));
}

export async function mockStartHealthcareService(
  serviceId: string,
): Promise<HealthcareServiceRecord | null> {
  const db = loadDb();
  const record = findAnyServiceRecord(db, serviceId);
  if (!record)
    return withDelay(null);

  const raw = record as Record<string, any>;
  record.status = normalizeMockServiceStatus(record.status);
  raw.start_date = raw.start_date || currentMockDateTime();
  raw.modified = currentMockDateTime();
  saveDb(db);

  return withDelay(mapMockHealthcareService(db, record));
}

export async function mockEndHealthcareService(
  serviceId: string,
): Promise<HealthcareServiceRecord | null> {
  const db = loadDb();
  const record = findAnyServiceRecord(db, serviceId);
  if (!record)
    return withDelay(null);

  const raw = record as Record<string, any>;
  raw.end_date = raw.end_date || currentMockDateTime();
  raw.modified = currentMockDateTime();
  saveDb(db);

  return withDelay(mapMockHealthcareService(db, record));
}

export async function mockSubmitHealthcareService(
  serviceId: string,
): Promise<HealthcareServiceRecord | null> {
  const db = loadDb();
  const record = findAnyServiceRecord(db, serviceId);
  if (!record)
    return withDelay(null);

  const raw = record as Record<string, any>;
  raw.docstatus = 1;
  raw.modified = currentMockDateTime();
  saveDb(db);

  return withDelay(mapMockHealthcareService(db, record));
}

export async function mockRateHealthcareService(
  serviceId: string,
  rating: number | null,
): Promise<HealthcareServiceRecord | null> {
  const db = loadDb();
  const record = findAnyServiceRecord(db, serviceId);
  if (!record)
    return withDelay(null);

  const raw = record as Record<string, any>;
  raw.rating = rating;
  raw.modified = currentMockDateTime();
  saveDb(db);

  return withDelay(mapMockHealthcareService(db, record));
}

export async function mockCreateHealthcareServiceRecord(
  payload: HealthcareServiceCreatePayload,
): Promise<{ serviceId: string } | null> {
  const db = loadDb();
  const serviceId = nextId("SVC-APPT-", db.services.map(item => item.id));

  db.services.unshift({
    id: serviceId,
    visitId: payload.visitId || "",
    orderId: null,
    definitionId: payload.careServiceId || null,
    serviceName: payload.serviceName,
    code: payload.itemCode || payload.careServiceId || payload.category || "",
    status: normalizeMockServiceStatus(payload.status || "pending"),
    priority: payload.priority || "Normal",
    assignedTo: payload.provider || payload.assignedTo || null,
    note: payload.description || payload.note || null,
    price: payload.price ?? null,
    performedAt: payload.startDate || payload.performedAt || null,
    pet_id: payload.petId,
    category: payload.category || null,
    care_service_id: payload.careServiceId || null,
    "care_service_id.service_name": payload.serviceName,
    pet_service_name: payload.serviceName,
    doctor: payload.doctor || null,
    visit: payload.visitId || null,
    doc_type: payload.sourceDocType || (payload.visitId ? "Visit" : null),
    document: payload.sourceDocument || payload.visitId || null,
    start_date: payload.startDate || null,
    end_date: payload.endDate || null,
    due_date: payload.dueDate || null,
    provider: payload.provider || payload.assignedTo || null,
    description: payload.description || payload.note || null,
    send_reminder: payload.sendReminder ? 1 : 0,
    rating: payload.rating ?? null,
    docstatus: 0,
    creation: currentMockDateTime(),
    modified: currentMockDateTime(),
  } as VisitServiceRecord);

  saveDb(db);
  return withDelay({ serviceId });
}

export async function mockGetBoardingUnits(): Promise<BoardingDetail[]> {
  const db = loadDb();
  const activeRecords = db.boarding
    .filter(record => isActiveBoardingRecord(record))
    .map(record => ({
      ...record,
      status: deriveOccupancyFromRecordStatus(record.recordStatus, record.status),
    }));

  return withDelay(activeRecords);
}

export async function mockGetBoardingDetail(boardingId: string): Promise<BoardingDetail | null> {
  const db = loadDb();
  return withDelay(db.boarding.find(item => item.id === boardingId) || null);
}

export async function mockGetBoardingRecords(
  filter: BoardingRecordFilterParam = {},
): Promise<FrappeListResult<BoardingRecordListItem>> {
  const db = loadDb();
  const filtered = db.boarding.filter((item) => {
    const matchesSearch = includesQuery([
      item.unitCode,
      item.unitLabel,
      item.pet?.name,
      item.guardian?.name,
      item.note,
      item.checkoutNotes,
    ], filter.search);

    const matchesStatus = sameOrEmpty(item.recordStatus, filter.status);
    const matchesPet = !filter.petId || item.pet?.id === filter.petId;
    const matchesGuardian = !filter.guardianId || item.guardian?.id === filter.guardianId;
    const matchesDates = matchesDateRange(item.checkIn || item.reservedAt, filter.dateFrom, filter.dateTo);

    return matchesSearch && matchesStatus && matchesPet && matchesGuardian && matchesDates;
  });

  return withDelay(
    paginate(
      sortByOrder(
        filtered.map(item => ({
          ...item,
          roomCode: item.unitCode,
          roomName: item.unitLabel,
          modified: item.reservedAt || item.checkIn || item.checkOut,
        })),
        filter.order_by,
      ) as unknown as BoardingRecordListItem[],
      filter.limit_start ?? 0,
      filter.limit_page_length ?? 10,
    ),
  );
}

export async function mockReserveBoardingRoom(
  payload: BoardingReservationPayload,
): Promise<{ roomId: string; boardingId: string } | null> {
  const db = loadDb();
  const rooms = getMockServiceRoomsSnapshot();

  const hasActiveRecord = db.boarding.some(record =>
    record.roomId === payload.roomId && isActiveBoardingRecord(record),
  );

  if (hasActiveRecord)
    return withDelay(null);

  const room = rooms.find(item => item.id === payload.roomId) || null;

  // `petIds` first, `petId` as the legacy fallback — mirroring the backend's precedence. The
  // mock only models the NOMINAL pet (the first), which is enough for the reserve flow; it does
  // not create one occupant row per pet, so multi-pet behaviour must be verified against a real
  // backend rather than in mock mode.
  const nominalPetId = payload.petIds?.[0] ?? payload.petId;
  const pet = [...db.caseSheets.map(item => item.pet), ...db.visits.map(item => item.pet)]
    .find(item => item.id === nominalPetId) || null;
  const guardian = [...db.caseSheets.map(item => item.guardian), ...db.visits.map(item => item.guardian)]
    .find(item => item.id === payload.guardianId) || null;

  if (!pet || !guardian || !room)
    return withDelay(null);

  const boardingId = nextId("BOARD-", db.boarding.map(item => item.id));
  const reservedAt = getNowIso().slice(0, 19).replace("T", " ");
  const stayDays = buildBoardingStayDays(payload.checkIn, payload.checkOut);
  const stayHours = buildBoardingStayHours(payload.checkIn, payload.checkOut);

  db.boarding.unshift({
    id: boardingId,
    boardingRecordId: asBoardingRecordId(boardingId),
    roomId: payload.roomId,
    unitCode: room.roomCode,
    unitLabel: room.roomName,
    roomImage: room.image ?? null,
    roomType: room.roomType || "Suite",
    status: "Reserved",
    recordStatus: "Reserved",
    pet,
    guardian,
    reservedAt,
    checkIn: payload.checkIn ?? null,
    checkOut: payload.checkOut ?? null,
    stayDays,
    stayHours,
    totalCost: null,
    deposit: null,
    balance: null,
    note: payload.note ?? null,
    notes: null,
    checkoutNotes: null,
    billingStatus: "Unbilled",
    billableItems: [],
  });

  syncBoardingBilling(db.boarding[0]);
  saveDb(db);
  return withDelay({ roomId: payload.roomId, boardingId });
}

export async function mockCheckInBoarding(
  payload: BoardingCheckInPayload,
): Promise<BoardingCheckInResult | null> {
  const db = loadDb();
  const record = db.boarding.find(item => item.id === payload.boardingId);

  if (!record)
    return withDelay(null);

  record.recordStatus = "Checked In";
  record.status = record.status === "Attention" ? "Attention" : "Occupied";
  /**
   * The server stamps arrival — UNLESS the desk stated when custody actually began.
   *
   * ⚠️ This mock implements the CONTRACT being asked for, not the endpoint as it stands today
   * (see `docs/README.boarding-backdated-check-in-backend-prompt.md`). That is the point of
   * mock mode: it is how the backdating flow — including the longer `stayDays` it produces and
   * the billing that follows — can be exercised before the endpoint accepts the key. The real
   * call has its own read-back guard for the case where it does not.
   */
  record.checkIn = /^\d{4}-\d{2}-\d{2}/.test(String(payload.checkIn ?? ""))
    ? String(payload.checkIn).slice(0, 10)
    : getNowIso().slice(0, 10);
  record.deposit = payload.deposit ?? record.deposit ?? null;
  if (payload.notes !== undefined) {
    record.notes = payload.notes ?? null;
  }
  if (payload.billableItems)
    record.billableItems = normalizeMockBillableItems(payload.billableItems, record.billableItems);

  record.stayDays = buildBoardingStayDays(record.checkIn, record.checkOut);
  record.stayHours = buildBoardingStayHours(record.checkIn, record.checkOut);
  syncBoardingBilling(record);

  // A deposit raises a submitted Payment Entry server-side. Mint one here too, so mock mode
  // exercises the same "confirm what was taken" surface the real backend drives.
  if (Number(record.deposit || 0) > 0 && !record.depositPaymentEntry)
    record.depositPaymentEntry = `PE-MOCK-${String(record.id).slice(-5)}`;

  saveDb(db);

  return withDelay({
    boardingId: record.id,
    deposit: record.deposit ?? null,
    depositPaymentEntry: record.depositPaymentEntry ?? null,
    // What was actually stored, read back off the record rather than echoed from the payload —
    // so the verification path behaves the same way in mock mode as it does against the server.
    checkInApplied: record.checkIn ?? null,
  });
}

export async function mockCheckOutBoarding(
  payload: BoardingCheckOutPayload,
): Promise<BoardingCheckOutResult | null> {
  const db = loadDb();
  const record = db.boarding.find(item => item.id === payload.boardingId);

  if (!record)
    return withDelay(null);

  if (payload.billableItems)
    record.billableItems = normalizeMockBillableItems(payload.billableItems, record.billableItems);
  if (payload.checkoutNote !== undefined) {
    record.checkoutNotes = payload.checkoutNote ?? null;
  }

  record.recordStatus = "Checked Out";
  record.status = "Available";
  // Server-stamped departure, as above.
  record.checkOut = getNowIso().slice(0, 10);
  record.stayDays = buildBoardingStayDays(record.checkIn, record.checkOut);
  record.stayHours = buildBoardingStayHours(record.checkIn, record.checkOut);
  syncBoardingBilling(record);

  saveDb(db);

  // The backend allocates the deposit against the invoice and reports what it could place.
  // Mock mode simplifies to ONE invoice per stay — the real backend reuses an invoice across
  // stays, which is precisely why `invoiceGrandTotal` must never be read as this stay's cost.
  const invoiceGrandTotal = record.totalCost ?? null;
  const deposit = record.deposit ?? null;
  const depositAllocated = deposit == null
    ? null
    : Math.min(deposit, invoiceGrandTotal ?? 0);

  return withDelay({
    boardingId: record.id,
    invoiceGrandTotal,
    deposit,
    depositAllocated,
    depositUnallocatedRemainder: deposit == null || depositAllocated == null
      ? null
      : Math.max(deposit - depositAllocated, 0),
    depositPaymentEntry: record.depositPaymentEntry ?? null,
    // At check-out the invoice is POSTED, never left as a draft — mock mode mirrors that so
    // the "collect the remainder here, not at the till" path can be exercised.
    invoiceSubmitted: true,
    invoiceOutstanding: Math.max((invoiceGrandTotal ?? 0) - mockBoardingTotalPaid(record), 0),
    totalPaid: mockBoardingTotalPaid(record),
    salesInvoice: `ACC-SINV-MOCK-${String(record.id).slice(-5)}`,
    payments: record.payments ?? [],
  });
}

/**
 * Everything paid on a mock stay: the deposit plus every recorded instalment.
 *
 * The deposit is counted from its own field rather than from `payments`, because check-in
 * mints its Payment Entry id without adding a row — exactly as the real backend behaved
 * before instalments existed.
 */
function mockBoardingTotalPaid(record: BoardingDetail) {
  const instalments = (record.payments ?? [])
    .reduce((sum, row) => sum + Number(row.paidAmount ?? 0), 0);

  return Number(record.deposit ?? 0) + instalments;
}

/**
 * Mock instalments, so the payment surface can be worked on without the backend.
 *
 * Mirrors the real endpoint's two behaviours: while the stay is open the money is an advance
 * (`unallocatedAmount` set, no `salesInvoice`); once it is checked out the payment applies to
 * the invoice and `invoiceOutstanding` drops. The refusals are mocked too — they are most of
 * what the dialog has to render.
 */
export async function mockRecordBoardingPayment(
  payload: BoardingPaymentPayload,
): Promise<BoardingPaymentResult> {
  const db = loadDb();
  const record = db.boarding.find(item => item.id === payload.boardingId);

  if (!record)
    throw new Error("Boarding record not found.");

  const amount = Number(payload.amount ?? 0);

  if (!Number.isFinite(amount) || amount <= 0)
    throw new Error("A payment amount greater than zero is required.");

  if (record.recordStatus === "Cancelled")
    throw new Error("Cancelled boarding records cannot take a payment.");

  const checkedOut = record.recordStatus === "Checked Out";
  const totalCost = record.totalCost ?? 0;
  const alreadyPaid = mockBoardingTotalPaid(record);
  const outstanding = Math.max(totalCost - alreadyPaid, 0);

  if (checkedOut && outstanding <= 0)
    throw new Error(`Sales Invoice <strong>${record.id}</strong> is already settled in full.`);

  if (checkedOut && amount > outstanding) {
    throw new Error(
      `Only <strong>${outstanding}</strong> is outstanding on Sales Invoice `
      + `<strong>${record.id}</strong>. Do not collect more than the bill.`,
    );
  }

  const paymentEntry = `PE-MOCK-${String(record.id).slice(-5)}-${(record.payments?.length ?? 0) + 1}`;

  record.payments = [
    ...(record.payments ?? []),
    {
      id: paymentEntry,
      postingDate: getNowIso().slice(0, 10),
      paidAmount: amount,
      // Before check-out there is no invoice to apply it to, so it sits as an advance.
      unallocatedAmount: checkedOut ? 0 : amount,
      paidTo: "531102 - POS 2",
      modeOfPayment: "Cash",
      owner: "Administrator",
      remarks: payload.note ?? `Boarding payment for ${record.id}.`,
    },
  ];

  saveDb(db);

  const totalPaid = mockBoardingTotalPaid(record);

  return withDelay({
    boardingId: record.id,
    paymentEntry,
    amount,
    account: "531102 - POS 2",
    posProfile: "Alkokh Vet Hotel - Cashier",
    totalPaid,
    totalCost,
    balance: totalCost - totalPaid,
    salesInvoice: checkedOut ? `ACC-SINV-MOCK-${String(record.id).slice(-5)}` : null,
    invoiceOutstanding: checkedOut ? Math.max(totalCost - totalPaid, 0) : 0,
    payments: record.payments ?? [],
    boarding: record,
  });
}

export async function mockSyncBoardingBillableItems(
  payload: BoardingBillableItemsPayload,
): Promise<boolean> {
  const db = loadDb();
  const record = db.boarding.find(item => item.id === payload.boardingId);

  if (!record)
    return withDelay(false);

  record.billableItems = normalizeMockBillableItems(payload.billableItems, record.billableItems);
  syncBoardingBilling(record);

  saveDb(db);
  return withDelay(true);
}

export async function mockSearchPets(
  search = "",
  page = 1,
  pageSize = 10,
  guardianId: string | null = null,
): Promise<{ items: HealthcareLookupOption<HealthcarePetSummary>[]; hasMore: boolean }> {
  const db = loadDb();
  const seen = new Map<string, {
    pet: HealthcarePetSummary;
    guardianIds: Set<string>;
  }>();

  [...db.appointments, ...db.caseSheets, ...db.visits, ...db.boarding]
    .forEach((item) => {
      const pet = item.pet;
      const relatedGuardianId = item.guardian?.id ?? null;

      if (!pet?.id)
        return;

      const existing = seen.get(pet.id) ?? {
        pet,
        guardianIds: new Set<string>(),
      };

      if (relatedGuardianId)
        existing.guardianIds.add(relatedGuardianId);

      seen.set(pet.id, existing);
    });

  const all = [...seen.values()]
    .filter(item =>
      !guardianId || item.guardianIds.has(guardianId),
    )
    .filter(item => includesQuery([item.pet.name, item.pet.species, item.pet.breed], search))
    .map<HealthcareLookupOption<HealthcarePetSummary>>(item => ({
      id: item.pet.id,
      label: item.pet.name,
      subtitle: [item.pet.species, item.pet.breed].filter(Boolean).join(" / "),
      image: item.pet.image,
      raw: item.pet,
    }));

  const start = (page - 1) * pageSize;
  const items = all.slice(start, start + pageSize);

  return withDelay({
    items,
    hasMore: start + pageSize < all.length,
  });
}

export async function mockSearchGuardians(
  search = "",
  page = 1,
  pageSize = 10,
): Promise<{ items: HealthcareLookupOption<HealthcareGuardianSummary>[]; hasMore: boolean }> {
  const db = loadDb();
  const seen = new Map<string, HealthcareGuardianSummary>();

  [...db.appointments.map(item => item.guardian), ...db.caseSheets.map(item => item.guardian), ...db.visits.map(item => item.guardian), ...db.boarding.map(item => item.guardian).filter(Boolean) as HealthcareGuardianSummary[]]
    .forEach((guardian) => {
      if (guardian?.id)
        seen.set(guardian.id, guardian);
    });

  const all = [...seen.values()]
    .filter(item => includesQuery([item.name, item.phone, item.email], search))
    .map<HealthcareLookupOption<HealthcareGuardianSummary>>(item => ({
      id: item.id,
      label: item.name,
      subtitle: item.phone || item.email || null,
      image: item.image,
      raw: item,
    }));

  const start = (page - 1) * pageSize;
  const items = all.slice(start, start + pageSize);

  return withDelay({
    items,
    hasMore: start + pageSize < all.length,
  });
}
